import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import {
  TrendingUp, TrendingDown, Euro, Receipt, Plus, Download,
  Trash2, Eye, X, Upload, CheckCircle, Clock, ArrowLeft,
  ChevronDown, ChevronRight, Search, HelpCircle, FileUp,
  AlertCircle, SkipForward, BarChart3, Repeat, Pencil, Pause, Play,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "credit_card_fee", label: "Credit Card Fee (AMEX)" },
  { value: "bank_fee",        label: "Bank Fee (Revolut)" },
  { value: "software",        label: "Software / Subscriptions" },
  { value: "development",     label: "Platform Development" },
  { value: "travel",          label: "Business Travel" },
  { value: "visa_cost",       label: "Visa Cost (supplier)" },
  { value: "marketing",       label: "Marketing / Advertising" },
  { value: "office",          label: "Office / Admin" },
  { value: "other",           label: "Other" },
];

const VAT_TYPES = [
  { value: "0% VAT",               label: "0% VAT" },
  { value: "Outside Scope EU VAT", label: "Outside Scope EU VAT" },
  { value: "21% VAT",              label: "21% VAT" },
  { value: "9% VAT",               label: "9% VAT" },
  { value: "Needs Review",         label: "Needs Review" },
];

const PAYMENT_ACCOUNTS = ["AMEX B", "Revolut Business", "Bank Transfer", "Cash"];
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];
const EXPENSE_TYPES = [
  { value: "mobile_phone",  label: "Mobile Phone" },
  { value: "home_internet", label: "Home Internet" },
  { value: "home_office",   label: "Home Office" },
  { value: "utilities",     label: "Utilities" },
  { value: "meals",         label: "Meals / Entertainment" },
  { value: "other",         label: "Other" },
];
const expTypeLabel = (v) => EXPENSE_TYPES.find(t => t.value === v)?.label || v;
const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_QUARTER = `Q${Math.floor(new Date().getMonth() / 3) + 1}`;

const getNetAmount = (item) => Number(item.amount || 0) - Number(item.vat_amount || 0);

const getDeductibleAmount = (item) => {
  const net = getNetAmount(item);
  const pct = item.business_use_percent != null ? Number(item.business_use_percent) : 100;
  return net * pct / 100;
};

function StatCard({ title, value, sub, icon: Icon, color = "purple" }) {
  const colors = {
    purple: "bg-purple-50 border-purple-200 text-purple-700",
    green:  "bg-green-50 border-green-200 text-green-700",
    red:    "bg-red-50 border-red-200 text-red-700",
    amber:  "bg-amber-50 border-amber-200 text-amber-700",
    blue:   "bg-blue-50 border-blue-200 text-blue-700",
  };
  return (
    <Card className={`border ${colors[color]}`}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium opacity-70 uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold mt-1">€{Number(value || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</p>
            {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
          </div>
          <Icon className="w-8 h-8 opacity-40" />
        </div>
      </CardContent>
    </Card>
  );
}

function ExpenseForm({ onSave, onCancel, initial = {}, extraCategories = [], onAddCategory = null }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    description: "", amount: "", currency: "EUR",
    category: "other", payment_account: "AMEX B",
    vat_amount: 0, vat_rate: 0, vat_type: "Outside Scope EU VAT",
    quarter: "", notes: "",
    receipt_data: null, receipt_filename: null,
    receipt_mime_type: null, receipt_url: null,
    business_use_percent: initial.business_use_percent != null ? String(initial.business_use_percent) : "",
    ...initial,
  });
  const [newCategoryInput, setNewCategoryInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const autoQuarter = (date) => `Q${Math.floor(new Date(date).getMonth() / 3) + 1}`;

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      try {
        const ext = file.name.includes(".") ? file.name.split(".").pop() : "jpg";
        const descPart = (form.description || "receipt").trim().replace(/[^a-zA-Z0-9 ]/g, "").replace(/\s+/g, "_");
        const dateObj = new Date(form.date);
        const day = String(dateObj.getDate()).padStart(2, "0");
        const mon = dateObj.toLocaleString("en-GB", { month: "short" });
        const yr  = dateObj.getFullYear();
        const datePart = `${day}${mon}${yr}`;
        const safeName = `${descPart}_${datePart}.${ext}`;

        const res = await axios.post(`${API}/uploads/receipt`, {
          file_data: base64, filename: safeName,
          mime_type: file.type, folder: "expense-receipts", invoice_number: "",
        }, { headers: getAuthHeaders() });

        if (res.data.url) {
          setForm(p => ({ ...p, receipt_url: res.data.url, receipt_filename: safeName, receipt_mime_type: file.type, receipt_data: null }));
          toast.success("Receipt uploaded ✓");
        } else {
          setForm(p => ({ ...p, receipt_data: base64, receipt_filename: safeName, receipt_mime_type: file.type, receipt_url: null }));
        }
      } catch {
        setForm(p => ({ ...p, receipt_data: base64, receipt_filename: file.name, receipt_mime_type: file.type, receipt_url: null }));
      }
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const clearReceipt = () => setForm(p => ({ ...p, receipt_data: null, receipt_filename: null, receipt_mime_type: null, receipt_url: null }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Date *</Label>
          <Input type="date" value={form.date}
            onChange={e => setForm(p => ({ ...p, date: e.target.value, quarter: autoQuarter(e.target.value) }))} />
        </div>
        <div>
          <Label className="text-xs">Quarter</Label>
          <select className="w-full border rounded-md px-3 py-2 text-sm"
            value={form.quarter} onChange={e => setForm(p => ({ ...p, quarter: e.target.value }))}>
            <option value="">Auto</option>
            {QUARTERS.map(q => <option key={q} value={q}>{q}</option>)}
          </select>
        </div>
      </div>
      <div>
        <Label className="text-xs">Description *</Label>
        <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
          placeholder="e.g. AMEX annual fee" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Amount (€) *</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={form.amount}
            onChange={e => {
              let val = e.target.value.replace(",", ".");
              setForm(p => ({ ...p, amount: val }));
            }}
            onBlur={e => {
              let val = e.target.value.replace(",", ".");
              setForm(p => ({ ...p, amount: val }));
            }}
            placeholder="0,00"
          />
        </div>
        <div>
          <Label className="text-xs">Category *</Label>
          <select className="w-full border rounded-md px-3 py-2 text-sm"
            value={form.category === "__add_new__" ? "__add_new__" : form.category}
            onChange={e => {
              if (e.target.value === "__add_new__") {
                setForm(p => ({ ...p, category: "__add_new__" }));
              } else {
                setForm(p => ({ ...p, category: e.target.value }));
              }
            }}>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            {extraCategories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            <option value="__add_new__">＋ Add new category…</option>
          </select>
          {form.category === "__add_new__" && (
            <input
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
              placeholder="New category name"
              value={newCategoryInput}
              onChange={e => setNewCategoryInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && newCategoryInput.trim()) {
                  const value = newCategoryInput.toLowerCase().trim().replace(/\s+/g, "_");
                  if (onAddCategory) onAddCategory(newCategoryInput);
                  setForm(p => ({ ...p, category: value }));
                  setNewCategoryInput("");
                }
              }}
              onBlur={() => {
                if (newCategoryInput.trim()) {
                  const value = newCategoryInput.toLowerCase().trim().replace(/\s+/g, "_");
                  if (onAddCategory) onAddCategory(newCategoryInput);
                  setForm(p => ({ ...p, category: value }));
                  setNewCategoryInput("");
                }
              }}
              autoFocus
            />
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Payment Account</Label>
          <select className="w-full border rounded-md px-3 py-2 text-sm"
            value={form.payment_account} onChange={e => setForm(p => ({ ...p, payment_account: e.target.value }))}>
            {PAYMENT_ACCOUNTS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">VAT Type</Label>
          <select className="w-full border rounded-md px-3 py-2 text-sm"
            value={form.vat_type} onChange={e => setForm(p => ({ ...p, vat_type: e.target.value }))}>
            {VAT_TYPES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
          </select>
        </div>
      </div>
      {form.vat_type === "21% VAT" && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">VAT Rate (%)</Label>
            <Input type="number" value={form.vat_rate}
              onChange={e => {
                const rate = parseFloat(e.target.value) || 0;
                const amt = parseFloat(form.amount) || 0;
                setForm(p => ({ ...p, vat_rate: rate, vat_amount: Math.round(amt * rate / (100 + rate) * 100) / 100 }));
              }} />
          </div>
          <div>
            <Label className="text-xs">VAT Amount (€)</Label>
            <Input type="number" step="0.01" value={form.vat_amount}
              onChange={e => setForm(p => ({ ...p, vat_amount: parseFloat(e.target.value) || 0 }))} />
          </div>
        </div>
      )}
      <div>
        <Label className="text-xs">Notes</Label>
        <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional" />
      </div>
      <div>
        <Label className="text-xs">Business Use % <span className="font-normal text-gray-400">(leave blank for 100%)</span></Label>
        <div className="flex items-center gap-2">
          <Input type="number" min="1" max="100" step="1"
            value={form.business_use_percent}
            onChange={e => setForm(p => ({ ...p, business_use_percent: e.target.value }))}
            placeholder="100" className="w-24" />
          {form.business_use_percent && Number(form.business_use_percent) < 100 && form.amount && (
            <span className="text-xs text-purple-600">
              Deductible: €{((parseFloat(form.amount)||0) * (parseFloat(form.business_use_percent)||100) / 100).toLocaleString("nl-NL", {minimumFractionDigits:2})}
            </span>
          )}
        </div>
      </div>
      <div>
        <Label className="text-xs">Receipt (image or PDF, max 10MB)</Label>
        <div className="mt-1">
          {form.receipt_filename ? (
            <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700 truncate flex-1">{form.receipt_filename}</span>
              <div className="flex items-center gap-1 shrink-0">
                {form.receipt_url && (
                  <a href={form.receipt_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">
                    <Eye className="w-3.5 h-3.5" />
                  </a>
                )}
                <button onClick={clearReceipt} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
              </div>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-400 hover:bg-purple-50 transition-colors">
              <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1" />
              <span className="text-sm text-gray-500">{uploading ? "Uploading..." : "Tap to upload receipt"}</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFile} />
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <Button className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
          onClick={() => onSave({ ...form, amount: parseFloat(form.amount) || 0, business_use_percent: form.business_use_percent ? parseFloat(form.business_use_percent) : null })}>
          Save Expense
        </Button>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

// Drop-in replacement for the VATReport + ReceiptCell section in AccountingPage.js
// Fully inline-editable table — click any cell to edit, Enter/blur to save

function ReceiptCell({ row, onUploaded, onAttach = null }) {
  const { getAuthHeaders } = useAuth();
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      const ext = file.name.includes(".") ? file.name.split(".").pop() : "pdf";
      try {
        const uploadRes = await axios.post(`${API}/uploads/receipt`, {
          file_data: base64,
          filename: `${row.invoice_number}.${ext}`,
          mime_type: file.type,
          folder: "supplier-receipts",
          invoice_number: row.invoice_number,
        }, { headers: getAuthHeaders() });
        if (uploadRes.data.url) {
          // onAttach is provided for service entries (patch service entry directly)
          // For regular bookings use the attach-receipt endpoint
          if (onAttach) {
            await onAttach(uploadRes.data.url);
          } else {
            await axios.post(`${API}/accounting/attach-receipt`, {
              invoice_number: row.invoice_number,
              supplier_receipt_url: uploadRes.data.url,
            }, { headers: getAuthHeaders() });
          }
          toast.success(`Receipt saved for ${row.invoice_number} ✓`);
          onUploaded(row.invoice_number, uploadRes.data.url);
        } else {
          toast.error("Upload failed");
        }
      } catch (err) {
        toast.error("Upload failed");
      }
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  // Visa supplier cost-item attachments (per-item receipt_url, may be multiple)
  const costItemUrls = row.cost_item_receipt_urls;
  if (costItemUrls?.length > 0) {
    return (
      <div className="inline-flex items-center gap-1">
        {costItemUrls.map((url, i) => (
          <a key={i} href={url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors"
            title={`View supplier receipt${costItemUrls.length > 1 ? ` (${i + 1}/${costItemUrls.length})` : ""} — ${row.invoice_number}`}>
            <Eye className="w-3.5 h-3.5" />
          </a>
        ))}
      </div>
    );
  }
  if (row.supplier_receipt_url) {
    return (
      <a href={row.supplier_receipt_url} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors"
        title={`View receipt — ${row.invoice_number}`}>
        <Eye className="w-3.5 h-3.5" />
      </a>
    );
  }
  // Visa rows with no attachment: show active upload when onAttach is provided (wired to
  // PATCH /visa-quotes/{id}/accounting), otherwise fall back to the disabled icon.
  if (row.type === "visa" && !onAttach) {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-50 text-gray-300"
        title="No supplier invoice attached — upload from Visa Quote → Supplier Costs">
        <Upload className="w-3 h-3" />
      </span>
    );
  }
  // Hotel rows: upload is managed in Hotel Accounting, not here
  if (row.type === "hotel_booking") {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-50 text-gray-300"
        title="Upload supplier invoice from Hotel Booking → Accounting">
        <Upload className="w-3 h-3" />
      </span>
    );
  }
  return (
    <>
      <button onClick={() => fileRef.current?.click()} disabled={uploading}
        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 hover:bg-purple-100 text-gray-400 hover:text-purple-600 transition-colors"
        title={`Upload receipt — ${row.invoice_number}`}>
        {uploading ? <span className="text-[9px] animate-pulse">…</span> : <Upload className="w-3 h-3" />}
      </button>
      <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleUpload} />
    </>
  );
}

// Single editable cell — click to edit, Enter/blur to save
function EditableCell({ value, field, invoiceNumber, onSave, type = "text", options = null, className = "" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  const inputRef = useRef();

  useEffect(() => { setVal(value ?? ""); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = async () => {
    setEditing(false);
    if (String(val) === String(value ?? "")) return; // no change
    await onSave(invoiceNumber, field, val);
  };

  if (editing) {
    if (options) {
      return (
        <select ref={inputRef} value={val}
          onChange={e => setVal(e.target.value)}
          onBlur={commit}
          className="w-full text-xs border border-purple-400 rounded px-1 py-0.5 bg-white outline-none min-w-[100px]">
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    return (
      <input ref={inputRef} type={type} value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setVal(value ?? ""); setEditing(false); } }}
        className="w-full text-xs border border-purple-400 rounded px-1 py-0.5 bg-white outline-none min-w-[80px]"
      />
    );
  }

  return (
    <span onClick={() => setEditing(true)}
      className={`cursor-pointer hover:bg-yellow-50 hover:outline hover:outline-1 hover:outline-yellow-300 rounded px-0.5 block w-full ${className} ${!val ? "text-gray-300 italic" : ""}`}
      title="Click to edit">
      {val || "—"}
    </span>
  );
}

function HistoryModal({ invoiceNumber, onClose, getAuthHeaders }) {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    axios.get(`${API}/accounting/audit/${invoiceNumber}`, { headers: getAuthHeaders() })
      .then(r => setEntries(r.data))
      .catch(() => setEntries([]));
  }, [invoiceNumber]);

  const fieldLabel = (f) => f.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-gray-800">Edit History — {invoiceNumber}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4">
          {entries === null && <p className="text-sm text-gray-400 text-center py-6">Loading…</p>}
          {entries && entries.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">No edit history yet.</p>
          )}
          {entries && entries.length > 0 && (
            <div className="space-y-2">
              {entries.map(e => (
                <div key={e.id} className="text-xs p-2 bg-gray-50 rounded border border-gray-100">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-purple-700">{fieldLabel(e.field)}</span>
                    <span className="text-gray-400">{e.changed_at ? e.changed_at.slice(0, 16).replace("T", " ") : ""}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="bg-red-50 text-red-700 px-1.5 py-0.5 rounded font-mono max-w-[40%] truncate" title={e.old_value}>{e.old_value || "—"}</span>
                    <span className="text-gray-400">→</span>
                    <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded font-mono max-w-[40%] truncate" title={e.new_value}>{e.new_value || "—"}</span>
                  </div>
                  <p className="text-gray-400 mt-1">by {e.changed_by}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RefundDetailsModal({ row, onClose, onSaved, getAuthHeaders }) {
  const cx = row.cancellation_record;

  // All hooks must be at the top level — never after a conditional return.
  const [form, setForm] = useState({
    refund_status:           row.refund_status || "",
    customer_refund_amount:  row.customer_refund_amount != null ? String(row.customer_refund_amount) : "",
    customer_refund_date:    row.customer_refund_date || "",
    supplier_refund_amount:  row.supplier_refund_amount != null ? String(row.supplier_refund_amount) : "",
    supplier_refund_date:    row.supplier_refund_date || "",
    refund_admin_fee_kept:   row.refund_admin_fee_kept != null ? String(row.refund_admin_fee_kept) : "",
    payment_fee_lost:        row.payment_fee_lost != null ? String(row.payment_fee_lost) : "",
    refund_notes:            row.refund_notes || "",
  });
  const [saving, setSaving] = useState(false);

  // --- Cancellation-record read-only view ---
  if (cx) {
    const customerPaid     = parseFloat(row.amount_received || 0);
    const customerRefunded = parseFloat(cx.customer_refund_amount || 0);
    const supplierCost     = parseFloat(row.supplier_cost || 0);
    const supplierRefund   = parseFloat(cx.supplier_refund_amount || 0);
    const cancelFee        = parseFloat(cx.cancellation_fee_retained || 0);
    const unrecovered      = parseFloat(cx.unrecovered_payment_fee || 0);
    const processingFee    = parseFloat(cx.refund_processing_fee || 0);
    const netPosition      = customerPaid - customerRefunded - supplierCost + supplierRefund + cancelFee - unrecovered - processingFee;

    const ro = (label, value) => (
      <div>
        <p className="text-xs text-gray-500 mb-0.5">{label}</p>
        <p className="text-sm font-medium text-gray-800 border border-gray-200 rounded px-2 py-1.5 bg-gray-50">{value || "—"}</p>
      </div>
    );

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b">
            <div>
              <h3 className="font-semibold text-gray-800">Cancellation Record — {row.invoice_number}</h3>
              <p className="text-xs text-gray-400 mt-0.5">{row.customer} · {row.payment_status}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-4 space-y-3">
            <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 text-xs text-orange-800">
              This invoice has a central cancellation record (
              {cx.cancellation_doc_number || cx.credit_note_number || cx.invoice_id}).
              Cancellation data is managed in the Invoice detail page.
              Legacy refund fields on this row are suppressed in all reports.
            </div>
            <div className="grid grid-cols-2 gap-3">
              {ro("Cancellation Date", cx.cancellation_date)}
              {ro("Credit Note / Doc", cx.credit_note_number || cx.cancellation_doc_number)}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {ro("Customer Refund Amount (€)", cx.customer_refund_amount != null ? `€${parseFloat(cx.customer_refund_amount).toFixed(2)}` : "")}
              {ro("Customer Refund Date", cx.customer_refund_date)}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {ro("Customer Refund Method", cx.customer_refund_method)}
              {ro("Supplier Refund Amount (€)", cx.supplier_refund_amount != null ? `€${parseFloat(cx.supplier_refund_amount).toFixed(2)}` : "")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {ro("Cancellation Fee Retained (€)", cx.cancellation_fee_retained != null ? `€${parseFloat(cx.cancellation_fee_retained).toFixed(2)}` : "")}
              {ro("Unrecovered Payment Fee (€)", cx.unrecovered_payment_fee != null ? `€${parseFloat(cx.unrecovered_payment_fee).toFixed(2)}` : "")}
            </div>
            {ro("Supplier Refund Notes", cx.supplier_refund_notes)}
            {ro("Internal Notes", cx.internal_notes)}

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Net Position</p>
              {[
                { label: "Customer paid",            value: customerPaid,     sign: "" },
                { label: "Customer refunded",         value: customerRefunded, sign: "−" },
                { label: "Supplier cost",             value: supplierCost,     sign: "−" },
                { label: "Supplier refund recovered", value: supplierRefund,   sign: "+" },
                { label: "Cancellation fee retained", value: cancelFee,        sign: "+" },
                { label: "Unrecovered payment fee",   value: unrecovered,      sign: "−" },
                { label: "Refund processing fee",     value: processingFee,    sign: "−" },
              ].map(({ label, value, sign }) => (
                <div key={label} className="flex justify-between text-xs text-gray-500">
                  <span>{sign} {label}</span>
                  <span className="font-mono">€{value.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between items-center pt-2 border-t border-gray-200 mt-2">
                <span className="text-sm font-semibold text-gray-700">Net</span>
                <span className={`text-lg font-bold ${netPosition >= 0 ? "text-green-700" : "text-red-600"}`}>
                  €{netPosition.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          <div className="p-4 border-t flex items-center justify-end">
            <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">Close</button>
          </div>
        </div>
      </div>
    );
  }

  // --- Legacy editable view (no cancellation_record) ---
  const customerPaid      = parseFloat(row.amount_received || 0);
  const customerRefunded  = parseFloat(form.customer_refund_amount || 0);
  const supplierCost      = parseFloat(row.supplier_cost || 0);
  const supplierRefund    = parseFloat(form.supplier_refund_amount || 0);
  const paymentFeeLost    = parseFloat(form.payment_fee_lost || 0);
  const adminFeeKept      = parseFloat(form.refund_admin_fee_kept || 0);
  const netPosition       = customerPaid - customerRefunded - supplierCost + supplierRefund + adminFeeKept - paymentFeeLost;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        refund_status:          form.refund_status || null,
        customer_refund_amount: form.customer_refund_amount !== "" ? parseFloat(form.customer_refund_amount) : null,
        customer_refund_date:   form.customer_refund_date || null,
        supplier_refund_amount: form.supplier_refund_amount !== "" ? parseFloat(form.supplier_refund_amount) : null,
        supplier_refund_date:   form.supplier_refund_date || null,
        refund_admin_fee_kept:  form.refund_admin_fee_kept !== "" ? parseFloat(form.refund_admin_fee_kept) : null,
        payment_fee_lost:       form.payment_fee_lost !== "" ? parseFloat(form.payment_fee_lost) : null,
        refund_notes:           form.refund_notes || null,
      };
      Object.keys(payload).forEach(k => payload[k] === null && delete payload[k]);

      if (row.type === "service") {
        await axios.patch(`${API}/accounting/service-entries/${row.service_entry_id}`, payload, { headers: getAuthHeaders() });
      } else {
        const bRes = await axios.get(`${API}/bookings`, { headers: getAuthHeaders() });
        const booking = bRes.data.find(b => (b.accounting || {}).invoice_number === row.invoice_number);
        if (!booking) { toast.error("Booking not found"); setSaving(false); return; }
        await axios.patch(`${API}/bookings/${booking.id}/accounting`, payload, { headers: getAuthHeaders() });
      }
      toast.success("Refund details saved ✓");
      onSaved();
    } catch (err) {
      toast.error("Save failed — " + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  const f = (label, field, type = "text") => (
    <div>
      <label className="block text-xs text-gray-500 mb-0.5">{label}</label>
      <input type={type} value={form[field]}
        onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
        className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-purple-400" />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="font-semibold text-gray-800">Refund Details — {row.invoice_number}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{row.customer} · {row.payment_status}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Refund Status</label>
            <select value={form.refund_status} onChange={e => setForm(p => ({ ...p, refund_status: e.target.value }))}
              className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-purple-400">
              <option value="">— Select —</option>
              <option>Refund Processed</option>
              <option>Refund Pending</option>
              <option>Partial Refund Processed</option>
              <option>Awaiting Supplier</option>
              <option>Disputed</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {f("Customer Refund Amount (€)", "customer_refund_amount", "number")}
            {f("Customer Refund Date", "customer_refund_date", "date")}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {f("Supplier Refund Amount (€)", "supplier_refund_amount", "number")}
            {f("Supplier Refund Date", "supplier_refund_date", "date")}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {f("Admin Fee Kept (€)", "refund_admin_fee_kept", "number")}
            {f("Payment Fee Lost (€)", "payment_fee_lost", "number")}
          </div>
          {f("Refund Notes", "refund_notes")}

          {/* Net position calculation */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Estimated Net Position</p>
            <p className="text-[10px] text-gray-400 mb-2">Estimate only — does not affect revenue table calculations.</p>
            {[
              { label: "Customer paid",           value: customerPaid,     sign: "" },
              { label: "Customer refunded",        value: customerRefunded, sign: "−" },
              { label: "Supplier cost",            value: supplierCost,     sign: "−" },
              { label: "Supplier refund recovered",value: supplierRefund,   sign: "+" },
              { label: "Admin fee kept",           value: adminFeeKept,     sign: "+" },
              { label: "Payment fee lost",         value: paymentFeeLost,   sign: "−" },
            ].map(({ label, value, sign }) => (
              <div key={label} className="flex justify-between text-xs text-gray-500">
                <span>{sign} {label}</span>
                <span className="font-mono">€{value.toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between items-center pt-2 border-t border-gray-200 mt-2">
              <span className="text-sm font-semibold text-gray-700">Est. Net</span>
              <span className={`text-lg font-bold ${netPosition >= 0 ? "text-green-700" : "text-red-600"}`}>
                €{netPosition.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
        <div className="p-4 border-t flex items-center justify-end gap-3">
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
          <button onClick={save} disabled={saving}
            className="bg-purple-600 hover:bg-purple-700 text-white text-sm px-4 py-2 rounded-lg font-medium disabled:opacity-60">
            {saving ? "Saving…" : "Save Refund Details"}
          </button>
        </div>
      </div>
    </div>
  );
}


const DASHBOARD_HELP = [
  { term: "Total Revenue", def: "Everything you invoiced in the selected period — all bookings plus any manual service entries (visa charges, non-flight extras, refunds, and correction entries)." },
  { term: "Supplier Cost", def: "What you paid to fulfil those bookings: airline fares, hotel costs, OTA payments, plus supplier costs on manual service entries." },
  { term: "Gross Profit", def: "Total Revenue minus Supplier Cost. Your margin before running costs. Calculated only for rows where a payment has been received." },
  { term: "Total Expenses", def: "Operating costs for the period: office, phone, software, bank charges, etc. Includes allocated expenses (e.g. 80% of your phone bill counted as business use). Does not include supplier costs — those are in Supplier Cost above." },
  { term: "Net Profit", def: "Gross Profit minus Total Expenses. What you actually keep after all costs." },
  { term: "Outstanding", def: "Revenue that has been invoiced but not yet received in your bank account." },
  { term: "Manual service entries", def: "Used to record visa income, non-flight services, manual refund/correction amounts, and older transactions. Negative amounts reduce revenue — this is correct for refunds and corrections." },
];

const VAT_HELP = [
  { term: "Customer Invoiced", def: "The total you charged customers for the period, excluding VAT. This is your omzet (turnover) for Dutch BTW purposes." },
  { term: "Bank Received", def: "Total payments that landed in your account. May differ from Customer Invoiced if some payments are still outstanding." },
  { term: "Supplier Cost", def: "Total paid to OTAs, airlines, and other suppliers linked to the rows in this report." },
  { term: "Gross Margin", def: "Bank Received minus Supplier Cost, for rows where a payment is recorded. Your earning on collected revenue." },
  { term: "BTW afdragen", def: "Output VAT you collected from customers and must pay to the Belastingdienst each quarter." },
  { term: "Inkoop BTW aftrekbaar", def: "VAT you paid on business expenses that you can reclaim. Includes regular expenses and the business-use share of allocated expenses (phone, internet, home office, etc.)." },
  { term: "Netto BTW betaling", def: "VAT to pay minus reclaimable VAT — the amount you actually owe each quarter. A negative number means you can reclaim from the Belastingdienst." },
  { term: "Allocated Expenses", def: "Costs split between personal and business use (phone, internet, home office). Only the agreed business percentage counts towards your deductions and VAT reclaim." },
  { term: "Why Dashboard and VAT Report may differ", def: "Dashboard covers all bookings and service entries for the selected year. VAT Report is tax-focused and may include rows from different periods depending on invoice dates. Small differences are normal." },
];

const GLOSSARY_TERMS = [
  { term: "Omzet / Customer Invoiced", def: "The amount you charged the customer, excluding VAT. This is your turnover (omzet) for Dutch BTW purposes." },
  { term: "Bank Received (Amt Rcvd)", def: "The amount that actually arrived in your bank account from this booking. May differ from the invoiced amount due to payment processor fees or timing." },
  { term: "Supplier / Supplier Cost", def: "The travel supplier (e.g. airline, hotel, OTA) you paid to fulfil this booking, and the amount you paid them." },
  { term: "Supplier # (Supplier Reference)", def: "The booking reference or ticket number given by the supplier. For flights this is the PNR or e-ticket number." },
  { term: "Paid With (Customer Payment Mode)", def: "How the customer paid you — for example AMEX, Revolut, bank transfer, Tikkie, or cash." },
  { term: "Supplier Paid (Supplier Paid Date)", def: "The date you paid the supplier. The badge shows Paid (green) if a date is filled in, or Unpaid (red) if the supplier has not yet been paid." },
  { term: "Date Rcvd", def: "The date you received the customer's payment in your bank account." },
  { term: "Amt Rcvd", def: "The amount received in your bank account for this booking. This is what you actually collected, which may differ from the invoiced amount." },
  { term: "Bank Fee", def: "Any transaction or bank fee charged on receiving this payment (e.g. Stripe, AMEX, Revolut fee). Deducted from Net Profit." },
  { term: "Gross Profit", def: "Bank Received minus Supplier Cost for this row. Only calculated where a bank payment is recorded." },
  { term: "Net Profit", def: "Gross Profit minus VAT due minus Bank Fees. Your actual take-home on this booking." },
  { term: "VAT Type", def: "How this booking is treated for Dutch BTW (VAT). Most flights are 0% VAT. 'Outside Scope EU VAT' means no Dutch VAT applies (e.g. international routes). 21% or 9% VAT applies to Dutch services. 'Needs Review' flags rows to check." },
  { term: "Processor", def: "The platform or payment method that processed the customer payment — Stripe, Tikkie, Revolut, AMEX, bank transfer, etc." },
  { term: "Proc. Ref (Processor Reference)", def: "The unique reference from the payment processor — e.g. a Stripe payment ID, Tikkie ID, Revolut transaction ID, or bank transfer reference. Not the supplier booking reference." },
  { term: "Payout Date", def: "The date the payment processor or bank settled / paid out the funds to your account." },
  { term: "Reconciled", def: "Whether you have checked this row against your bank statement or processor report. Set to Yes once verified, No if there is a discrepancy." },
  { term: "BTW Afdragen", def: "VAT you collected from customers and must pay to the Dutch tax authority (Belastingdienst)." },
  { term: "Inkoop BTW (Input VAT reclaimable)", def: "VAT you paid on business expenses that you can reclaim from the Belastingdienst." },
  { term: "Netto BTW betaling (Net VAT payable)", def: "VAT collected minus input VAT reclaimable. This is the amount you must pay to (or can reclaim from) the Belastingdienst each quarter." },
];

// ── Excel Import Modal ───────────────────────────────────────────────────────

const ROW_TYPE_LABELS = { normal: "Expense", allocated: "Allocated", unclear: "Unclear" };
const ROW_TYPE_COLORS = {
  normal:   "bg-blue-50 text-blue-700 border-blue-200",
  allocated:"bg-purple-50 text-purple-700 border-purple-200",
  unclear:  "bg-amber-50 text-amber-700 border-amber-200",
};
const STATUS_COLORS = {
  new:       "bg-green-50 text-green-700",
  duplicate: "bg-red-50 text-red-600",
};

function ImportModal({ onClose, onImported }) {
  const { getAuthHeaders } = useAuth();
  const [step, setStep]               = useState("upload"); // upload | preview | result
  const [uploading, setUploading]     = useState(false);
  const [preview, setPreview]         = useState(null);
  const [selectedIdxs, setSelectedIdxs] = useState(new Set());
  const [importMode, setImportMode]   = useState("allocated"); // "allocated" | "normal" | "selected"
  const [confirming, setConfirm]      = useState(false);
  const [result, setResult]           = useState(null);
  const fileRef = useRef();

  const computeSelection = (rows, mode) => {
    const idxs = new Set();
    rows.forEach((r, i) => {
      if (r.status === "duplicate" || r.row_type === "unclear") return;
      if (mode === "allocated" && r.row_type === "allocated") idxs.add(i);
      if (mode === "normal"    && r.row_type === "normal")    idxs.add(i);
    });
    return idxs;
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) { toast.error("Please upload an .xlsx or .xls file"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/accounting/import/preview`, fd, {
        headers: { ...getAuthHeaders() },
      });
      setPreview(res.data);
      setImportMode("allocated");
      setSelectedIdxs(computeSelection(res.data.rows, "allocated"));
      setStep("preview");
    } catch (e) {
      toast.error("Parse error: " + (e.response?.data?.detail || e.message));
    }
    setUploading(false);
  };

  const handleModeChange = (mode) => {
    setImportMode(mode);
    if (mode !== "selected") setSelectedIdxs(computeSelection(preview.rows, mode));
  };

  const toggleRow = (i) => {
    setImportMode("selected");
    setSelectedIdxs(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const toggleAll = () => {
    if (!preview) return;
    const allIdxs = preview.rows.map((_, i) => i);
    const allSelected = allIdxs.every(i => selectedIdxs.has(i));
    setImportMode("selected");
    setSelectedIdxs(allSelected ? new Set() : new Set(allIdxs));
  };

  const handleConfirm = async () => {
    const rowsToImport = preview.rows.filter((_, i) => selectedIdxs.has(i));
    if (rowsToImport.length === 0) { toast.error("No rows selected"); return; }
    const hasDupes = rowsToImport.some(r => r.status === "duplicate");
    setConfirm(true);
    try {
      const res = await axios.post(`${API}/accounting/import/confirm`, {
        filename: preview.filename,
        rows: rowsToImport,
        force_duplicates: hasDupes,
      }, { headers: getAuthHeaders() });
      setResult(res.data);
      setStep("result");
      onImported();
    } catch (e) {
      toast.error("Import failed: " + (e.response?.data?.detail || e.message));
    }
    setConfirm(false);
  };

  const unclearRows = preview?.rows.filter(r => r.row_type === "unclear") || [];
  const selectedCount = selectedIdxs.size;
  const allSelected = preview ? preview.rows.length > 0 && preview.rows.every((_, i) => selectedIdxs.has(i)) : false;
  const someSelected = preview ? preview.rows.some((_, i) => selectedIdxs.has(i)) : false;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <div>
            <h3 className="font-semibold text-gray-800">Import from Excel</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {step === "upload"  && "Upload your BTW dossier Excel file"}
              {step === "preview" && `Preview — ${preview.filename}`}
              {step === "result"  && "Import complete"}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-4">

          {/* ── Step 1: Upload ── */}
          {step === "upload" && (
            <div className="space-y-4">
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
                className="border-2 border-dashed border-purple-300 rounded-xl p-10 text-center cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors">
                {uploading
                  ? <p className="text-sm text-purple-600 font-medium">Reading file…</p>
                  : <>
                      <FileUp className="w-10 h-10 mx-auto text-purple-400 mb-3" />
                      <p className="text-sm font-medium text-gray-700">Drop your Excel file here</p>
                      <p className="text-xs text-gray-400 mt-1">or click to browse — .xlsx / .xls</p>
                    </>
                }
              </div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
                onChange={e => handleFile(e.target.files[0])} />
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-purple-700 mb-1">Default import behaviour</p>
                <ul className="text-xs text-purple-600 space-y-0.5 list-disc list-inside">
                  <li>Allocated expenses (Odido, internet, home office) → <strong>selected by default</strong></li>
                  <li>Normal expenses (fees, subscriptions, etc.) → unselected by default</li>
                  <li>Duplicates and unclear rows → unselected by default</li>
                  <li>Section headings, labels, and formula cells → automatically ignored</li>
                  <li>Allocated rows must have a recognizable month to be importable</li>
                  <li>Select/deselect individual rows in the preview before confirming</li>
                </ul>
              </div>
            </div>
          )}

          {/* ── Step 2: Preview ── */}
          {step === "preview" && preview && (
            <div className="space-y-4">
              {/* Summary badges */}
              <div className="flex gap-3 flex-wrap items-center">
                <span className="text-xs font-semibold bg-green-50 text-green-700 border border-green-200 px-3 py-1 rounded-full">
                  {preview.new_count} importable
                </span>
                {preview.duplicate_count > 0 && (
                  <span className="text-xs font-semibold bg-red-50 text-red-600 border border-red-200 px-3 py-1 rounded-full">
                    {preview.duplicate_count} duplicates
                  </span>
                )}
                {preview.unclear_count > 0 && (
                  <span className="text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-3 py-1 rounded-full">
                    {preview.unclear_count} needs review
                  </span>
                )}
                {preview.ignored_count > 0 && (
                  <span className="text-xs font-semibold bg-gray-100 text-gray-500 border border-gray-200 px-3 py-1 rounded-full">
                    {preview.ignored_count} ignored (headings/labels)
                  </span>
                )}
                <span className="text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 px-3 py-1 rounded-full">
                  {selectedCount} selected
                </span>
              </div>

              {/* Summary-only mode — no structured rows found */}
              {preview.summary_detected && preview.rows.filter(r => r.row_type === "allocated").length === 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-blue-800">Allocated expense summary detected — no monthly rows found</p>
                      <p className="text-xs text-blue-600 mt-0.5">{preview.summary_detected.note}</p>
                    </div>
                  </div>
                  {preview.summary_detected.phone_amounts && (
                    <div className="flex items-center gap-2 text-xs text-blue-700">
                      <span className="font-medium">Phone / Odido amounts:</span>
                      <span className="font-mono">{preview.summary_detected.phone_amounts.map(v => `€${v.toFixed(2)}`).join(" · ")}</span>
                    </div>
                  )}
                  {preview.summary_detected.internet_amounts && (
                    <div className="flex items-center gap-2 text-xs text-blue-700">
                      <span className="font-medium">Internet amounts:</span>
                      <span className="font-mono">{preview.summary_detected.internet_amounts.map(v => `€${v.toFixed(2)}`).join(" · ")}</span>
                    </div>
                  )}
                  <p className="text-xs text-blue-600 italic">
                    Use the amounts above as a reference and add monthly entries manually via the Allocated tab.
                  </p>
                </div>
              )}

              {/* Import mode selector */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-gray-600">Quick select:</span>
                {[
                  { key: "allocated", label: "Allocated expenses only" },
                  { key: "normal",    label: "Normal expenses only" },
                  { key: "selected",  label: "Manual selection" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => handleModeChange(key)}
                    className={`text-xs px-3 py-1 rounded-full border font-medium transition-colors ${
                      importMode === key
                        ? "bg-purple-600 text-white border-purple-600"
                        : "bg-white text-gray-600 border-gray-300 hover:border-purple-400 hover:text-purple-600"
                    }`}>
                    {label}
                  </button>
                ))}
              </div>

              {/* Preview table */}
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                          onChange={toggleAll}
                          className="rounded cursor-pointer"
                          title="Select / deselect all"
                        />
                      </th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">#</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Type</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Status</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Month</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Supplier</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Description</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-600">Excl VAT</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-600">VAT</th>
                      <th className="px-3 py-2 text-right font-semibold text-gray-600">Biz %</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-600">Category / Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => {
                      const checked = selectedIdxs.has(i);
                      const isDupe = r.status === "duplicate";
                      const isUnclear = r.row_type === "unclear";
                      return (
                        <tr
                          key={i}
                          onClick={() => toggleRow(i)}
                          className={`border-b border-gray-100 cursor-pointer transition-colors
                            ${checked ? "bg-purple-50/60" : isDupe ? "opacity-40" : isUnclear ? "bg-amber-50/40" : "hover:bg-gray-50"}`}>
                          <td className="px-3 py-1.5 text-center" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRow(i)}
                              className="rounded cursor-pointer"
                            />
                          </td>
                          <td className="px-3 py-1.5 text-gray-400">{r.row_num}</td>
                          <td className="px-3 py-1.5">
                            <span className={`px-2 py-0.5 rounded-full border text-[11px] font-semibold ${ROW_TYPE_COLORS[r.row_type] || ""}`}>
                              {ROW_TYPE_LABELS[r.row_type] || r.row_type}
                            </span>
                          </td>
                          <td className="px-3 py-1.5">
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_COLORS[r.status] || ""}`}>
                              {isDupe ? "Duplicate" : "New"}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-gray-600">{r.month || "—"}</td>
                          <td className="px-3 py-1.5 text-gray-600 max-w-[100px] truncate">{r.supplier || "—"}</td>
                          <td className="px-3 py-1.5 text-gray-700 max-w-[160px] truncate">{r.description || "—"}</td>
                          <td className="px-3 py-1.5 text-right text-gray-700">€{(r.amount_excl_vat || 0).toFixed(2)}</td>
                          <td className="px-3 py-1.5 text-right text-gray-500">€{(r.vat_amount || 0).toFixed(2)}</td>
                          <td className="px-3 py-1.5 text-right text-gray-500">{r.business_pct != null ? `${r.business_pct}%` : "—"}</td>
                          <td className="px-3 py-1.5 text-gray-500 text-[11px]">{r.expense_type_or_category || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Unclear explanation */}
              {unclearRows.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-amber-700">{unclearRows.length} row(s) need manual review</p>
                    <p className="text-xs text-amber-600 mt-0.5">These could not be classified automatically. Add them manually via Add Expense or Add Allocated Expense.</p>
                  </div>
                </div>
              )}

              {/* Duplicate-in-selection warning */}
              {preview.rows.some((r, i) => selectedIdxs.has(i) && r.status === "duplicate") && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700 font-medium">
                    You have selected {preview.rows.filter((r, i) => selectedIdxs.has(i) && r.status === "duplicate").length} duplicate row(s).
                    These will be imported again and may create duplicate records.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Result ── */}
          {step === "result" && result && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">{result.imported_count}</p>
                  <p className="text-xs text-green-600 mt-1">Records imported</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-gray-500">{result.skipped_count}</p>
                  <p className="text-xs text-gray-400 mt-1">Skipped</p>
                </div>
                <div className={`rounded-lg p-4 text-center border ${result.error_count > 0 ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"}`}>
                  <p className={`text-2xl font-bold ${result.error_count > 0 ? "text-red-600" : "text-gray-400"}`}>{result.error_count}</p>
                  <p className="text-xs text-gray-400 mt-1">Errors</p>
                </div>
              </div>

              {result.imported.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Imported</p>
                  {result.imported.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0" />
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${r.type === "normal" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"}`}>{r.type}</span>
                      <span className="text-gray-700">{r.description || r.id}</span>
                    </div>
                  ))}
                </div>
              )}
              {result.errors.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs font-semibold text-red-600 uppercase tracking-wide">Errors</p>
                  {result.errors.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-red-600">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      Row {e.row_num}: {e.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t shrink-0 bg-gray-50">
          {step === "upload" && (
            <p className="text-xs text-gray-400">Supported: .xlsx, .xls from any BTW dossier layout</p>
          )}
          {step === "preview" && (
            <>
              <button onClick={() => setStep("upload")} className="text-xs text-gray-500 hover:text-gray-700">← Re-upload</button>
              <Button
                className="bg-purple-600 hover:bg-purple-700 text-white"
                onClick={handleConfirm}
                disabled={confirming || selectedCount === 0}>
                {confirming ? "Importing…" : `Import ${selectedCount} selected row${selectedCount !== 1 ? "s" : ""}`}
              </Button>
            </>
          )}
          {step === "result" && (
            <Button className="bg-purple-600 hover:bg-purple-700 text-white ml-auto" onClick={onClose}>Done</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function HowCalculatedModal({ defaultTab = "dashboard", onClose }) {
  const [tab, setTab] = useState(defaultTab);
  const tabs = [
    { key: "dashboard", label: "Dashboard" },
    { key: "vat",       label: "VAT Report" },
    { key: "terms",     label: "Table Terms" },
  ];
  const items = tab === "dashboard" ? DASHBOARD_HELP : tab === "vat" ? VAT_HELP : GLOSSARY_TERMS;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <div>
            <h3 className="font-semibold text-gray-800">How are these totals calculated?</h3>
            <p className="text-xs text-gray-400 mt-0.5">Plain-language guide for travel agents</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex border-b shrink-0">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${tab === t.key ? "border-purple-500 text-purple-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="overflow-y-auto p-4 space-y-3">
          {items.map(({ term, def }) => (
            <div key={term} className="border border-gray-100 rounded-lg p-3">
              <p className="text-xs font-semibold text-purple-700 mb-1">{term}</p>
              <p className="text-xs text-gray-600 leading-relaxed">{def}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VATReport({ data, limit, onLimitChange, onReceiptUploaded, onRowUpdated }) {
  const { getAuthHeaders } = useAuth();

  // ── All hooks BEFORE any early return (React rules) ──
  const [showHelp, setShowHelp] = useState(false);
  // Scroll sync: top phantom bar ↔ table wrapper
  const topScrollRef  = useRef(null);
  const bodyScrollRef = useRef(null);
  const syncingRef    = useRef(false);

  useEffect(() => {
    const top  = topScrollRef.current;
    const body = bodyScrollRef.current;
    if (!top || !body) return;
    const onTop = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      body.scrollLeft = top.scrollLeft;
      syncingRef.current = false;
    };
    const onBody = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      top.scrollLeft = body.scrollLeft;
      syncingRef.current = false;
    };
    top.addEventListener("scroll", onTop);
    body.addEventListener("scroll", onBody);
    return () => {
      top.removeEventListener("scroll", onTop);
      body.removeEventListener("scroll", onBody);
    };
  }, []);
  const [showAddService, setShowAddService] = useState(false);
  const [refundModal, setRefundModal] = useState(null);
  const [historyInvoice, setHistoryInvoice] = useState(null);
  
  // Column filters state
  const [filters, setFilters] = useState({
    invoice_number: "",
    customer: "",
    description: "",
    vat_type: "",
    quarter: "",
    supplier: "",
    payment_status: ""
  });
  
  const emptyRow = (inv="") => ({
    invoice_number: inv,
    date: new Date().toISOString().split("T")[0],
    customer_name: "", description: "", amount: "",
    supplier_cost: "0", payment_mode: "", payment_status: "Paid",
    date_received: "", amount_received: "", vat_type: "0% VAT",
    supplier_paid_date: "", linked_booking: "", notes: "",
  });
  const [addSvcForm, setAddSvcForm] = useState(emptyRow());
  const [savingSvc, setSavingSvc] = useState(false);

  const openAddService = async () => {
    setShowAddService(true);
    try {
      const res = await axios.get(`${API}/accounting/next-invoice-number`, { headers: getAuthHeaders() });
      setAddSvcForm(f => ({ ...f, invoice_number: res.data.next }));
    } catch {
      // endpoint not yet deployed — user can type manually
    }
  };

  if (!data) return null;
  const s = data.summary;
  const fmt = (v) => Number(v || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 });

  const VAT_TYPES = ["0% VAT", "Outside Scope EU VAT", "21% VAT", "9% VAT", "Needs Review"];
  const STATUSES  = ["Paid", "Pending", "Full Refund", "Partially Refunded"];
  
  // Apply filters to rows
  const filteredRows = data.revenue_rows.filter(row => {
    if (filters.invoice_number && !row.invoice_number?.toLowerCase().includes(filters.invoice_number.toLowerCase())) return false;
    if (filters.customer && !row.customer?.toLowerCase().includes(filters.customer.toLowerCase())) return false;
    if (filters.description && !row.description?.toLowerCase().includes(filters.description.toLowerCase())) return false;
    if (filters.vat_type && row.vat_type !== filters.vat_type) return false;
    if (filters.quarter && row.quarter !== filters.quarter) return false;
    if (filters.supplier && !row.supplier_name?.toLowerCase().includes(filters.supplier.toLowerCase())) return false;
    if (filters.payment_status && row.payment_status !== filters.payment_status) return false;
    return true;
  });
  
  const clearFilters = () => setFilters({
    invoice_number: "", customer: "", description: "", vat_type: "", quarter: "", supplier: "", payment_status: ""
  });
  
  const hasActiveFilters = Object.values(filters).some(v => v);

  // Save a field for hotel_booking rows — routes to the correct hotel endpoint
  const saveHotelField = async (hotelId, field, newValue) => {
    try {
      // Fields stored at top-level of the hotel document
      const topLevelFields = new Set(["supplier_name", "supplier_reference", "amount_excl_vat"]);
      // Fields stored in the accounting subdocument
      const accountingFields = new Set([
        "vat_type", "payment_status", "payment_mode", "supplier_paid_date",
        "date_received", "amount_received", "bank_fee", "invoice_date", "quarter",
        "description", "comment",
      ]);

      if (field === "customer") { toast.error("Customer name cannot be edited here"); return; }

      if (field === "supplier_cost") {
        // Write to accounting.cost_items so that VAT report reads the same value it writes
        const amount = parseFloat(newValue) || 0;
        const payload = { cost_items: [{ id: Date.now().toString(), description: "Supplier cost", amount }] };
        await axios.patch(`${API}/hotels/${hotelId}/accounting`, payload, { headers: getAuthHeaders() });
      } else if (topLevelFields.has(field)) {
        const apiField = field === "amount_excl_vat" ? "selling_price" : field;
        const sendValue = field === "amount_excl_vat" ? (parseFloat(newValue) || 0) : newValue;
        await axios.patch(`${API}/hotels/${hotelId}`, { [apiField]: sendValue }, { headers: getAuthHeaders() });
      } else if (accountingFields.has(field)) {
        const apiField = field === "description" ? "comment" : field;
        const numericFields = ["amount_received", "bank_fee"];
        const sendValue = numericFields.includes(field) ? (parseFloat(newValue) || 0) : newValue;
        const payload = { [apiField]: sendValue };
        if (field === "invoice_date" && newValue) {
          const m = new Date(newValue).getMonth();
          payload.quarter = `Q${Math.floor(m / 3) + 1}`;
        }
        await axios.patch(`${API}/hotels/${hotelId}/accounting`, payload, { headers: getAuthHeaders() });
      } else {
        toast.error(`${field} cannot be edited here`);
        return;
      }
      toast.success("Hotel booking updated ✓");
      onRowUpdated();
    } catch (err) {
      toast.error("Save failed — " + (err.response?.data?.detail || err.message));
    }
  };

  const saveField = async (invoiceNumber, field, newValue) => {
    try {
      // Map display field names to API field names
      const fieldMap = {
        customer:           null, // passenger name — not patchable via accounting
        supplier_name:      "supplier_name",
        supplier_reference: "supplier_reference",
        vat_type:           "vat_type",
        payment_status:     "payment_status",
        payment_mode:       "payment_mode",
        supplier_paid_date: "supplier_paid_date",
        date_received:      "date_received",
        amount_received:    "amount_received",
        bank_fee:           "bank_fee",
        invoice_date:       "invoice_date",
        description:        "item_type",
        quarter:            "quarter",
        amount_excl_vat:              "_selling_price",   // special — patches booking selling_price
        supplier_cost:                "_base_price",       // special — patches booking base_price
        payment_processor:            "payment_processor",
        payment_processor_reference:  "payment_processor_reference",
        payout_date:                  "payout_date",
        reconciled:                   "reconciled",
      };

      const apiField = fieldMap[field];
      if (!apiField) { toast.error(`${field} cannot be edited here`); return; }

      // Find booking ID via invoice number
      const bRes = await axios.get(`${API}/bookings`, { headers: getAuthHeaders() });
      const booking = bRes.data.find(b => (b.accounting || {}).invoice_number === invoiceNumber);
      if (!booking) { toast.error("Cannot edit here — use the VAT report inline edit"); return; }

      if (apiField === "_selling_price") {
        await axios.patch(`${API}/bookings/${booking.id}`, { selling_price: parseFloat(newValue) }, { headers: getAuthHeaders() });
      } else if (apiField === "_base_price") {
        await axios.patch(`${API}/bookings/${booking.id}`, { base_price: parseFloat(newValue) }, { headers: getAuthHeaders() });
      } else {
        const numericFields = ["amount_received", "bank_fee", "vat_percent"];
        const sendValue = numericFields.includes(apiField) ? (parseFloat(newValue) || 0) : newValue;
        const payload = { [apiField]: sendValue };
        // Auto-recalculate quarter when invoice_date changes
        if (apiField === "invoice_date") {
          const m = new Date(newValue).getMonth();
          payload.quarter = `Q${Math.floor(m / 3) + 1}`;
        }
        await axios.patch(`${API}/bookings/${booking.id}/accounting`, payload, { headers: getAuthHeaders() });
      }

      toast.success(`${invoiceNumber} updated ✓`);
      onRowUpdated();
    } catch (err) {
      toast.error("Save failed — " + (err.response?.data?.detail || err.message));
    }
  };

  const saveServiceField = async (entryId, field, newValue) => {
    try {
      await axios.patch(`${API}/accounting/service-entries/${entryId}`, { [field]: newValue }, { headers: getAuthHeaders() });
      toast.success("Updated ✓");
      onRowUpdated();
    } catch (err) {
      toast.error("Save failed — " + (err.response?.data?.detail || err.message));
    }
  };

  // Save an editable field for visa rows — routes to PATCH /visa-quotes/{id}/accounting.
  // Never falls through to the flight-booking saveField handler.
  const saveVisaField = async (visaQuoteId, field, newValue) => {
    try {
      // Map VAT-report display field names to the visa accounting API field names.
      // Fields not listed here are blocked (non-editable inline).
      const fieldMap = {
        invoice_date:       "invoice_date",
        description:        "item_type",
        vat_type:           "vat_type",
        quarter:            "quarter",
        supplier_name:      "supplier_name",
        supplier_reference: "supplier_reference",
        payment_mode:       "payment_mode",
        supplier_paid_date: "supplier_paid_date",
        date_received:      "date_received",
        amount_received:    "amount_received",
        bank_fee:           "bank_fee",
        payment_status:     "payment_status",
        supplier_receipt_url: "supplier_receipt_url",
      };
      const apiField = fieldMap[field];
      if (!apiField) { toast.error(`${field} cannot be edited here`); return; }
      const numericFields = ["amount_received", "bank_fee"];
      const payload = { [apiField]: numericFields.includes(apiField) ? (parseFloat(newValue) || 0) : newValue };
      await axios.patch(`${API}/visa-quotes/${visaQuoteId}/accounting`, payload, { headers: getAuthHeaders() });
      toast.success("Visa updated ✓");
      onRowUpdated();
    } catch (err) {
      toast.error("Save failed — " + (err.response?.data?.detail || err.message));
    }
  };

  // Normalize a raw supplier_paid_date value to a YYYY-MM-DD string, or "" if invalid/absent.
  // Used by both the badge and the date display cell so they are always consistent.
  const normalizeSupplierDate = (raw) => {
    if (!raw) return "";
    const s = String(raw).trim().slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "";
  };

  const supplierBadge = (r) => {
    const hasCost = parseFloat(r.supplier_cost) > 0;
    const hasPaid = !!normalizeSupplierDate(r.supplier_paid_date);
    if (!hasCost) return null;
    if (hasPaid) return (
      <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-semibold bg-green-100 text-green-700 mt-0.5 whitespace-nowrap">
        ✓ Supplier Paid
      </span>
    );
    return (
      <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-semibold bg-red-100 text-red-700 mt-0.5 whitespace-nowrap">
        Supplier Unpaid
      </span>
    );
  };

  const deleteServiceEntry = async (entryId) => {
    if (!window.confirm("Delete this service entry?")) return;
    try {
      await axios.delete(`${API}/accounting/service-entries/${entryId}`, { headers: getAuthHeaders() });
      toast.success("Deleted");
      onRowUpdated();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const submitServiceEntry = async () => {
    if (!addSvcForm.customer_name.trim()) { toast.error("Enter customer name"); return; }
    if (!addSvcForm.description.trim())   { toast.error("Enter description");   return; }
    if (!addSvcForm.amount)               { toast.error("Enter amount");         return; }
    if (!addSvcForm.invoice_number.trim()){ toast.error("Enter invoice number"); return; }
   const duplicate = data.revenue_rows.some(r => r.invoice_number?.trim() === addSvcForm.invoice_number.trim());
    if (duplicate) { toast.error(`${addSvcForm.invoice_number} already exists in this report`); return; }
    setSavingSvc(true);
    try {
     await axios.post(`${API}/accounting/service-entries`, {
        ...addSvcForm,
        amount:          parseFloat(addSvcForm.amount) || 0,
        supplier_cost:   parseFloat(addSvcForm.supplier_cost) || 0,
        amount_received: addSvcForm.amount_received ? parseFloat(addSvcForm.amount_received) : null,
   }, { headers: getAuthHeaders(), timeout: 30000 });
      toast.success("Service entry added ✓");
      setShowAddService(false);
      setAddSvcForm(emptyRow(""));
      onRowUpdated();
} catch (err) {
      if (!err.response) {
        // Network/timeout error — backend may have saved successfully, refresh to check
        toast.warning("Connection slow — checking if entry was saved...");
        await new Promise(r => setTimeout(r, 2000));
        onRowUpdated();
        setShowAddService(false);
        setAddSvcForm(emptyRow(""));
      } else {
        const msg = err.response?.data?.detail || err.response?.statusText || err.message;
        toast.error(`Save failed (${err.response?.status || "?"}): ${msg}`);
      }
    } finally { setSavingSvc(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-gray-800">
          Dutch BTW Report — {data.quarter ? `${data.quarter} ${data.year}` : `All Quarters ${data.year}`}
        </h3>
        <button onClick={() => setShowHelp(true)}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-purple-700 border border-gray-200 hover:border-purple-300 rounded-md px-2.5 py-1.5 transition-colors">
          <HelpCircle className="w-3.5 h-3.5" />
          How are these totals calculated?
        </button>
      </div>

      {/* ── Summary: Money Flow + Your Earnings ── */}
      {/* Totals come from backend summary over the full unpaginated dataset */}
      {(() => {
        const omzet        = s.total_revenue_excl_vat || 0;
        const bankCollect  = s.total_bank_received    || 0;
        const supplierCost = s.total_supplier_cost    || 0;
        const bankFee      = s.total_bank_fees        || 0;
        const totalVat     = s.total_vat_collected    || 0;
        const grossProfit  = s.gross_profit           || 0;
        const netProfit    = s.net_profit             || 0;

        const SumCard = ({ label, sub, value, color }) => (
          <div className={`p-3 rounded-lg border ${
            color === "green"  ? "bg-green-50 border-green-200" :
            color === "red"    ? "bg-red-50 border-red-200" :
            color === "blue"   ? "bg-blue-50 border-blue-200" :
            color === "amber"  ? "bg-amber-50 border-amber-200" :
            "bg-purple-50 border-purple-200"}`}>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
            <p className={`text-lg font-bold mt-0.5 ${
              color === "green"  ? "text-green-700" :
              color === "red"    ? "text-red-600" :
              color === "blue"   ? "text-blue-700" :
              color === "amber"  ? "text-amber-600" :
              "text-purple-700"}`}>
              €{Number(value).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>
          </div>
        );

        return (
          <div className="space-y-2">
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 pl-0.5">Money Flow</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <SumCard label="Customer Invoiced" sub="Omzet excl. BTW"   value={omzet}        color="purple" />
                <SumCard label="Bank Received"      sub="Collected in bank" value={bankCollect}  color="green" />
                <SumCard label="Supplier Cost"      sub="Paid to OTAs"      value={supplierCost} color="red" />
                <SumCard label="Bank Fees"           sub="Transaction costs" value={bankFee}      color="amber" />
              </div>
            </div>
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 pl-0.5">Your Earnings</p>
              <div className="grid grid-cols-3 gap-2">
                <SumCard label="Gross Margin" sub="Bank − Suppliers"        value={grossProfit} color={grossProfit >= 0 ? "green" : "red"} />
                <SumCard label="VAT Due"      sub="BTW to pay / reclaim"    value={totalVat}    color="amber" />
                <SumCard label="Net Margin"   sub="Gross − VAT − Bank Fees" value={netProfit}   color={netProfit >= 0 ? "blue" : "red"} />
              </div>
            </div>
          </div>
        );
      })()}

      <p className="text-[10px] text-gray-400 italic">
        VAT Report totals are calculated server-side from all matching VAT rows. The table below may show only the displayed rows.
      </p>

      {/* ── BTW boxes ── */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: "BTW afdragen",            value: s.total_vat_collected,         color: "amber" },
          { label: "Inkoop BTW (aftrekbaar)", value: s.total_input_vat_reclaimable, color: "blue" },
          { label: "Netto BTW betaling",      value: s.net_vat_payable,             color: s.net_vat_payable > 0 ? "red" : "green" },
        ].map(item => (
          <div key={item.label} className={`p-3 rounded-lg ${
            item.color === "green" ? "bg-green-50 border border-green-200" :
            item.color === "red"   ? "bg-red-50 border border-red-200" :
            item.color === "amber" ? "bg-amber-50 border border-amber-200" :
            "bg-blue-50 border border-blue-200"}`}>
            <p className="text-xs text-gray-500">{item.label}</p>
            <p className="text-lg font-bold">€{fmt(item.value)}</p>
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs font-semibold text-gray-600 uppercase">Revenue Transactions</p>
            <span className="text-[10px] text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">Click any cell to edit</span>
            <button onClick={openAddService}
              className="text-[10px] font-semibold bg-amber-500 hover:bg-amber-600 text-white px-2 py-0.5 rounded-full transition-colors">
              + Add Service / Extra
            </button>
            {hasActiveFilters && (
              <button onClick={clearFilters}
                className="text-[10px] font-semibold bg-red-100 hover:bg-red-200 text-red-600 px-2 py-0.5 rounded-full transition-colors">
                ✕ Clear Filters
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Show:</label>
            <select value={limit} onChange={(e) => onLimitChange(parseInt(e.target.value))}
              className="border rounded-md px-2 py-1 text-xs">
              <option value={500}>500</option>
              <option value={1000}>1000</option>
              <option value={5000}>All</option>
            </select>
            <span className="text-xs text-gray-500">{filteredRows.length} of {data.total_rows}{hasActiveFilters ? " (filtered)" : ""}</span>
          </div>
        </div>

        {/* Top horizontal scrollbar — synced with the table container */}
        <div
          ref={topScrollRef}
          className="overflow-x-scroll overflow-y-hidden rounded-t border border-b-0 border-gray-200 bg-gray-50"
          style={{ height: 18 }}
        >
          <div style={{ minWidth: "1200px", height: 1 }} />
        </div>

        <div
          ref={bodyScrollRef}
          className="overflow-auto rounded-b border border-gray-200"
          style={{ maxHeight: "70vh" }}
        >
          <table className="w-full text-xs border-collapse min-w-[1200px]">
            <thead className="sticky top-0 z-20">
              <tr className="bg-purple-50 text-purple-700 text-left">
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap sticky left-0 z-30 bg-purple-50">Invoice #</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap text-center sticky left-[72px] z-30 bg-purple-50">Receipt</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap sticky left-[120px] z-30 bg-purple-50">Customer</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap">Date</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap">Item</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap">VAT Type</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap text-right">Omzet</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap">Quarter</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap">Supplier</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap">Supplier #</th>
                <th className="pl-4 pr-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap text-right">Cost</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap">Paid With</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap">Supplier Paid</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap">Date Rcvd</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap text-right">Amt Rcvd</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap text-right">Bank Fee</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap text-right">Gross Profit</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap text-right">Net Profit</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap">Status</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap">Processor</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap">Proc. Ref</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap">Payout Date</th>
                <th className="px-2 py-2 font-semibold border-b border-gray-200 whitespace-nowrap text-center">Reconciled</th>
              </tr>
              {/* Filter row */}
              <tr className="bg-gray-100">
                <th className="px-1 py-1 border-b border-gray-200 sticky left-0 z-30 bg-gray-100">
                  <input type="text" value={filters.invoice_number} onChange={(e) => setFilters(f => ({...f, invoice_number: e.target.value}))}
                    placeholder="Filter..." className="w-full px-1 py-0.5 text-[10px] border rounded" />
                </th>
                <th className="px-1 py-1 border-b border-gray-200 sticky left-[72px] z-30 bg-gray-100"></th>
                <th className="px-1 py-1 border-b border-gray-200 sticky left-[120px] z-30 bg-gray-100">
                  <input type="text" value={filters.customer} onChange={(e) => setFilters(f => ({...f, customer: e.target.value}))}
                    placeholder="Filter..." className="w-full px-1 py-0.5 text-[10px] border rounded" />
                </th>
                <th className="px-1 py-1 border-b border-gray-200"></th>
                <th className="px-1 py-1 border-b border-gray-200">
                  <input type="text" value={filters.description} onChange={(e) => setFilters(f => ({...f, description: e.target.value}))}
                    placeholder="Filter..." className="w-full px-1 py-0.5 text-[10px] border rounded" />
                </th>
                <th className="px-1 py-1 border-b border-gray-200">
                  <select value={filters.vat_type} onChange={(e) => setFilters(f => ({...f, vat_type: e.target.value}))}
                    className="w-full px-1 py-0.5 text-[10px] border rounded bg-white">
                    <option value="">All</option>
                    {VAT_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </th>
                <th className="px-1 py-1 border-b border-gray-200"></th>
                <th className="px-1 py-1 border-b border-gray-200">
                  <select value={filters.quarter} onChange={(e) => setFilters(f => ({...f, quarter: e.target.value}))}
                    className="w-full px-1 py-0.5 text-[10px] border rounded bg-white">
                    <option value="">All</option>
                    <option value="Q1">Q1</option>
                    <option value="Q2">Q2</option>
                    <option value="Q3">Q3</option>
                    <option value="Q4">Q4</option>
                  </select>
                </th>
                <th className="px-1 py-1 border-b border-gray-200">
                  <input type="text" value={filters.supplier} onChange={(e) => setFilters(f => ({...f, supplier: e.target.value}))}
                    placeholder="Filter..." className="w-full px-1 py-0.5 text-[10px] border rounded" />
                </th>
                <th className="px-1 py-1 border-b border-gray-200"></th>
                <th className="px-1 py-1 border-b border-gray-200"></th>
                <th className="px-1 py-1 border-b border-gray-200"></th>
                <th className="px-1 py-1 border-b border-gray-200"></th>
                <th className="px-1 py-1 border-b border-gray-200"></th>
                <th className="px-1 py-1 border-b border-gray-200"></th>
                <th className="px-1 py-1 border-b border-gray-200"></th>
                <th className="px-1 py-1 border-b border-gray-200"></th>
                <th className="px-1 py-1 border-b border-gray-200"></th>
                <th className="px-1 py-1 border-b border-gray-200">
                  <select value={filters.payment_status} onChange={(e) => setFilters(f => ({...f, payment_status: e.target.value}))}
                    className="w-full px-1 py-0.5 text-[10px] border rounded bg-white">
                    <option value="">All</option>
                    {STATUSES.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </th>
                <th className="px-1 py-1 border-b border-gray-200"></th>
                <th className="px-1 py-1 border-b border-gray-200"></th>
                <th className="px-1 py-1 border-b border-gray-200"></th>
                <th className="px-1 py-1 border-b border-gray-200"></th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r, i) => {
                const isRefund      = Number(r.amount_excl_vat) < 0;
                const isSvc         = r.type === "service";
                const isHotel       = r.type === "hotel_booking";
                const isVisa        = r.type === "visa";
                const isNeedsReview = r.vat_type === "Needs Review";
                const rowBg = isRefund ? "bg-red-50" : isNeedsReview ? "bg-amber-50" : i % 2 === 0 ? "bg-white" : "bg-gray-50";
                const inv = r.invoice_number;
                // Route writes to the correct endpoint per row type.
                // Visa rows always use saveVisaField — they must never fall through to saveField
                // (which searches db.bookings and would silently fail for visa-quote records).
                const hotelSave = isHotel ? ((_inv, field, val) => saveHotelField(r.hotel_id, field, val)) : null;
                const visaSave  = isVisa  ? ((_inv, field, val) => saveVisaField(r.visa_quote_id, field, val)) : null;
                const defaultSave = isSvc ? null : (isHotel ? hotelSave : (isVisa ? visaSave : saveField));
                return (
                  <tr key={i} className={`${rowBg} hover:bg-yellow-50 transition-colors`}>
                    <td className={`px-2 py-1 border-b border-gray-100 font-mono text-gray-600 whitespace-nowrap sticky left-0 z-10 ${rowBg}`}>
                      <div className="flex items-center gap-1">
                        <span>{r.invoice_number}</span>
                        {!isSvc && (
                          <button onClick={() => setHistoryInvoice(r.invoice_number)} title="Edit history"
                            className="text-gray-200 hover:text-purple-500 transition-colors shrink-0">
                            <Clock className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className={`px-2 py-1 border-b border-gray-100 text-center sticky left-[72px] z-10 ${rowBg}`}>
                      {isSvc
                        ? <ReceiptCell row={r}
                            onUploaded={(inv, url) => {}}
                            onAttach={(url) => saveServiceField(r.service_entry_id, "supplier_receipt_url", url)} />
                        : isVisa
                          ? <ReceiptCell row={r}
                              onUploaded={onReceiptUploaded}
                              onAttach={(url) => saveVisaField(r.visa_quote_id, "supplier_receipt_url", url)} />
                          : <ReceiptCell row={r} onUploaded={onReceiptUploaded} />
                      }
                    </td>
                    <td className={`px-2 py-1 border-b border-gray-100 max-w-[110px] sticky left-[120px] z-10 ${rowBg}`}>
                      <EditableCell value={r.customer} field="customer" invoiceNumber={inv}
                        onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "customer_name", v) : defaultSave} />
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 whitespace-nowrap">
                      <EditableCell value={r.date ? r.date.slice(0,10) : ""} field="invoice_date" invoiceNumber={inv} type="date"
                        onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "date", v) : defaultSave} />
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 max-w-[130px]">
                      <EditableCell value={r.description} field="description" invoiceNumber={inv}
                        onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "description", v) : defaultSave} />
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100">
                      <EditableCell value={r.vat_type} field="vat_type" invoiceNumber={inv} options={VAT_TYPES} onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "vat_type", v) : defaultSave} />
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 text-right whitespace-nowrap">
                      <EditableCell value={String(r.amount_excl_vat)} field="amount_excl_vat" invoiceNumber={inv} type="number"
                        className={`text-right font-bold ${isRefund ? "text-red-600" : "text-gray-800"}`}
                        onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "amount", parseFloat(v)) : defaultSave} />
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100">
                      <EditableCell value={r.quarter} field="quarter" invoiceNumber={inv} options={["Q1","Q2","Q3","Q4"]} onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "quarter", v) : defaultSave} />
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 max-w-[110px]">
                      <EditableCell value={r.supplier_name} field="supplier_name" invoiceNumber={inv} onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "supplier_name", v) : defaultSave} />
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 max-w-[100px]">
                      <EditableCell value={r.supplier_reference} field="supplier_reference" invoiceNumber={inv} className="font-mono" onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "confirmation_reference", v) : defaultSave} />
                    </td>
                    <td className="pl-4 pr-2 py-1 border-b border-gray-100 text-right whitespace-nowrap">
                      <EditableCell value={String(r.supplier_cost)} field="supplier_cost" invoiceNumber={inv} type="number" className="text-right"
                        onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "supplier_cost", parseFloat(v)) : defaultSave} />
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 max-w-[130px]">
                      <EditableCell value={r.payment_mode} field="payment_mode" invoiceNumber={inv}
                        onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "payment_mode", v) : defaultSave} />
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 whitespace-nowrap">
                      <EditableCell value={normalizeSupplierDate(r.supplier_paid_date)} field="supplier_paid_date" invoiceNumber={inv} type="date" onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "supplier_paid_date", v) : defaultSave} />
                      {supplierBadge(r)}
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 whitespace-nowrap">
                      <EditableCell value={r.date_received ? r.date_received.slice(0,10) : ""} field="date_received" invoiceNumber={inv} type="date" onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "date_received", v) : defaultSave} />
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 text-right whitespace-nowrap">
                      <EditableCell value={String(r.amount_received)} field="amount_received" invoiceNumber={inv} type="number" className="text-right"
                        onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "amount_received", parseFloat(v)) : defaultSave} />
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 text-right whitespace-nowrap">
                      <EditableCell value={String(r.bank_fee || 0)} field="bank_fee" invoiceNumber={inv} type="number" className="text-right" onSave={isSvc ? () => {} : defaultSave} />
                    </td>
                    <td className={`px-2 py-1 border-b border-gray-100 text-right font-semibold whitespace-nowrap ${Number(r.gross_profit) >= 0 ? "text-green-700" : "text-red-600"}`}>
                      €{fmt(r.gross_profit)}
                    </td>
                    <td className={`px-2 py-1 border-b border-gray-100 text-right font-semibold whitespace-nowrap ${Number(r.net_profit) >= 0 ? "text-green-700" : "text-red-600"}`}>
                      €{fmt(r.net_profit)}
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100">
                      <div className="flex items-center gap-1">
                        <EditableCell value={r.payment_status} field="payment_status" invoiceNumber={inv} options={STATUSES}
                          onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "payment_status", v) : defaultSave} />
                        <button
                          onClick={() => setRefundModal(r)}
                          title="Refund details"
                          className={`shrink-0 w-5 h-5 rounded flex items-center justify-center transition-colors ${
                            ["Full Refund", "Partially Refunded"].includes(r.payment_status)
                              ? "text-orange-500 hover:bg-orange-50"
                              : "text-gray-200 hover:text-gray-400 hover:bg-gray-50"
                          }`}>
                          <Receipt className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 max-w-[100px]">
                      <EditableCell value={r.payment_processor} field="payment_processor" invoiceNumber={inv}
                        onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "payment_processor", v) : defaultSave} />
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 max-w-[110px]">
                      <EditableCell value={r.payment_processor_reference || ""} field="payment_processor_reference" invoiceNumber={inv}
                        onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "payment_processor_reference", v) : defaultSave} />
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 whitespace-nowrap">
                      <EditableCell value={r.payout_date ? r.payout_date.slice(0,10) : ""} field="payout_date" invoiceNumber={inv} type="date"
                        onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "payout_date", v) : defaultSave} />
                    </td>
                    <td className="px-2 py-1 border-b border-gray-100 text-center">
                      <EditableCell value={r.reconciled || ""} field="reconciled" invoiceNumber={inv} options={["", "Yes", "No"]}
                        onSave={isSvc ? (id,f,v) => saveServiceField(r.service_entry_id, "reconciled", v) : defaultSave}
                        className={`text-center text-[10px] font-semibold ${r.reconciled === "Yes" ? "text-green-700" : r.reconciled === "No" ? "text-red-600" : ""}`} />
                    </td>
                  </tr>
                );
              })}
              {/* ── Inline new service entry row ── */}
              {showAddService && (
                <tr className="bg-amber-50 border-y-2 border-amber-300">
                  <td className="px-2 py-1 whitespace-nowrap"><input value={addSvcForm.invoice_number} onChange={e=>setAddSvcForm(p=>({...p,invoice_number:e.target.value}))} placeholder="TBF-361" className="w-full text-xs border border-amber-400 rounded px-1 py-0.5 font-mono font-bold min-w-[70px]" /></td>
                  <td className="px-2 py-1 text-center">
                    <button onClick={() => setShowAddService(false)} className="text-red-400 hover:text-red-600 text-xs font-bold" title="Cancel">✕</button>
                  </td>
                  <td className="px-2 py-1"><input value={addSvcForm.customer_name} onChange={e=>setAddSvcForm(p=>({...p,customer_name:e.target.value}))} placeholder="Customer *" className="w-full text-xs border border-amber-300 rounded px-1 py-0.5 min-w-[90px]" /></td>
                  <td className="px-2 py-1 whitespace-nowrap"><input type="date" value={addSvcForm.date} onChange={e=>setAddSvcForm(p=>({...p,date:e.target.value}))} className="text-xs border border-amber-300 rounded px-1 py-0.5" /></td>
                  <td className="px-2 py-1"><input value={addSvcForm.description} onChange={e=>setAddSvcForm(p=>({...p,description:e.target.value}))} placeholder="Description *" className="w-full text-xs border border-amber-300 rounded px-1 py-0.5 min-w-[120px]" /></td>
                  <td className="px-2 py-1">
                    <select value={addSvcForm.vat_type} onChange={e=>setAddSvcForm(p=>({...p,vat_type:e.target.value}))} className="text-xs border border-amber-300 rounded px-1 py-0.5 w-full">
                      {["0% VAT","Outside Scope EU VAT","21% VAT","9% VAT","Needs Review"].map(o=><option key={o}>{o}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap"><input type="number" step="0.01" value={addSvcForm.amount} onChange={e=>setAddSvcForm(p=>({...p,amount:e.target.value}))} placeholder="0.00 *" className="w-full text-xs border border-amber-300 rounded px-1 py-0.5 text-right font-bold min-w-[60px]" /></td>
                  <td className="px-2 py-1 text-xs text-amber-500 italic">auto</td>
                  <td className="px-2 py-1"><input value={addSvcForm.linked_booking} onChange={e=>setAddSvcForm(p=>({...p,linked_booking:e.target.value}))} placeholder="TBF-357" className="w-full text-xs border border-amber-300 rounded px-1 py-0.5 font-mono min-w-[70px]" /></td>
                  <td className="px-2 py-1"></td>
                  <td className="px-2 py-1 whitespace-nowrap"><input type="number" step="0.01" value={addSvcForm.supplier_cost} onChange={e=>setAddSvcForm(p=>({...p,supplier_cost:e.target.value}))} placeholder="0.00" className="w-full text-xs border border-amber-300 rounded px-1 py-0.5 text-right min-w-[60px]" /></td>
                  <td className="px-2 py-1"><input value={addSvcForm.payment_mode} onChange={e=>setAddSvcForm(p=>({...p,payment_mode:e.target.value}))} placeholder="Bank Transfer" className="w-full text-xs border border-amber-300 rounded px-1 py-0.5 min-w-[90px]" /></td>
                  <td className="px-2 py-1 whitespace-nowrap"><input type="date" value={addSvcForm.supplier_paid_date} onChange={e=>setAddSvcForm(p=>({...p,supplier_paid_date:e.target.value}))} className="text-xs border border-amber-300 rounded px-1 py-0.5" /></td>
                  <td className="px-2 py-1 whitespace-nowrap"><input type="date" value={addSvcForm.date_received} onChange={e=>setAddSvcForm(p=>({...p,date_received:e.target.value}))} className="text-xs border border-amber-300 rounded px-1 py-0.5" /></td>
                  <td className="px-2 py-1 whitespace-nowrap"><input type="number" step="0.01" value={addSvcForm.amount_received} onChange={e=>setAddSvcForm(p=>({...p,amount_received:e.target.value}))} placeholder="0.00" className="w-full text-xs border border-amber-300 rounded px-1 py-0.5 text-right min-w-[60px]" /></td>
                  <td className="px-2 py-1 text-xs text-gray-400">—</td>
                  <td className="px-2 py-1 text-xs text-gray-400">—</td>
                  <td className="px-2 py-1 text-xs text-gray-400">—</td>
                  <td className="px-2 py-1">
                    <select value={addSvcForm.payment_status} onChange={e=>setAddSvcForm(p=>({...p,payment_status:e.target.value}))} className="text-xs border border-amber-300 rounded px-1 py-0.5">
                      <option>Paid</option><option>Pending</option>
                    </select>
                  </td>
                  <td className="px-2 py-1 text-xs text-gray-400">—</td>
                  <td className="px-2 py-1 text-xs text-gray-400">—</td>
                  <td className="px-2 py-1 text-xs text-gray-400">—</td>
                  <td className="px-2 py-1 text-xs text-gray-400">—</td>
                </tr>
              )}
              {showAddService && (
                <tr className="bg-amber-50">
                  <td colSpan={23} className="px-4 py-2 text-right">
                    <button onClick={() => { setShowAddService(false); setAddSvcForm(emptyRow("")); }} className="text-xs text-slate-500 hover:text-slate-700 mr-3">Cancel</button>
                    <button onClick={submitServiceEntry} disabled={savingSvc}
                      className="text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white px-4 py-1.5 rounded-md disabled:opacity-60">
                      {savingSvc ? "Saving…" : "Save Entry"}
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="bg-purple-50 font-bold text-purple-800 text-xs">
                <td colSpan={6} className="px-2 py-2 uppercase tracking-wide sticky left-0 z-10 bg-purple-50">Totals</td>
                <td className="px-2 py-2 text-right">€{fmt(data.revenue_rows.reduce((acc,r) => acc + (r.amount_excl_vat||0), 0))}</td>
                <td colSpan={3} />
                <td className="pl-4 pr-2 py-2 text-right">€{fmt(data.revenue_rows.reduce((acc,r) => acc + (r.supplier_cost||0), 0))}</td>
                <td colSpan={2} />
                <td />
                <td className="px-2 py-2 text-right">€{fmt(data.revenue_rows.reduce((acc,r) => acc + (r.amount_received||0), 0))}</td>
                <td />
                {(() => {
                  const totalOmzet    = data.revenue_rows.reduce((acc,r) => acc + (r.amount_excl_vat||0), 0);
                  const totalCost     = data.revenue_rows.reduce((acc,r) => acc + (r.supplier_cost||0), 0);
                  const totalVat2     = data.revenue_rows.reduce((acc,r) => acc + (r.vat_amount||0), 0);
                  // Cash-basis: profit and fees only from rows where payment was received.
                  // Deducting bank fees from unreceived rows would reduce net without a matching gross.
                  const recvRows = data.revenue_rows.filter(r => parseFloat(r.amount_received) !== 0);
                  const gross = recvRows.reduce((a,r) => a + parseFloat(r.amount_received||0), 0)
                              - recvRows.reduce((a,r) => a + (r.supplier_cost||0), 0);
                  const totalFee = recvRows.reduce((a,r) => a + (r.bank_fee||0), 0);
                  const net   = gross - totalVat2 - totalFee;
                  return (
                    <>
                      <td className={`px-2 py-2 text-right ${gross >= 0 ? "text-green-700" : "text-red-600"}`}>€{fmt(gross)}</td>
                      <td className={`px-2 py-2 text-right ${net >= 0 ? "text-green-700" : "text-red-600"}`}>€{fmt(net)}</td>
                    </>
                  );
                })()}
                <td /><td colSpan={4} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>



      {data.expense_rows.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-600 uppercase">Deductible Expenses</p>
            <span className="text-xs text-gray-500">{data.expense_rows.length} of {data.total_expense_rows}</span>
          </div>
          <div className="space-y-1">
            {data.expense_rows.map((e, i) => (
              <div key={i} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded">
                <span className="text-gray-500 w-24 shrink-0">{e.date}</span>
                <span className="text-gray-700 flex-1 truncate mx-2">{e.description}</span>
                <span className="font-bold text-red-600 w-20 text-right">-€{getNetAmount(e).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.allocated_expense_rows || []).length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs font-semibold text-gray-600 uppercase">Allocated Expenses (incl. in VAT report)</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Partial-business expenses — phone, internet, home office etc.</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">{data.allocated_expense_rows.length} entries</p>
              <p className="text-xs font-semibold text-green-700">Claimable VAT: €{(data.summary.allocated_claimable_vat || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
          <div className="space-y-1">
            {data.allocated_expense_rows.map((e, i) => (
              <div key={i} className="flex items-center justify-between text-xs p-2 bg-blue-50 rounded border border-blue-100">
                <span className="text-gray-500 w-20 shrink-0 font-mono">{e.month}</span>
                <span className="text-gray-500 w-28 shrink-0">{expTypeLabel(e.expense_type)}</span>
                <span className="text-gray-700 flex-1 truncate mx-2">{e.supplier}{e.description ? ` — ${e.description}` : ""}</span>
                <span className="text-blue-700 font-semibold w-24 text-right">€{Number(e.deductible_business_cost || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
                <span className="text-green-700 font-bold w-20 text-right ml-2">VAT: €{Number(e.claimable_vat || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {refundModal && (
        <RefundDetailsModal
          row={refundModal}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setRefundModal(null)}
          onSaved={() => { setRefundModal(null); onRowUpdated(); }}
        />
      )}

      {historyInvoice && (
        <HistoryModal
          invoiceNumber={historyInvoice}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setHistoryInvoice(null)}
        />
      )}

      {showHelp && <HowCalculatedModal defaultTab="vat" onClose={() => setShowHelp(false)} />}
    </div>
  );
}

function AllocatedExpenseForm({ initial = {}, onSave, onCancel }) {
  const { getAuthHeaders } = useAuth();
  const defaultMonth = new Date().toISOString().slice(0, 7);

  // For existing records without contribution_basis: if they had a contribution saved,
  // honour the old excl_vat formula. New records default to incl_vat.
  const defaultBasis = initial.contribution_basis ||
    (initial.private_or_employer_contribution != null ? "excl_vat" : "incl_vat");

  const [form, setForm] = useState({
    expense_type:                    initial.expense_type || "mobile_phone",
    month:                           initial.month || defaultMonth,
    supplier:                        initial.supplier || "",
    invoice_reference:               initial.invoice_reference || "",
    description:                     initial.description || "",
    total_amount_incl_vat:           initial.total_amount_incl_vat != null ? String(initial.total_amount_incl_vat) : "",
    amount_excl_vat:                 initial.amount_excl_vat != null ? String(initial.amount_excl_vat) : "",
    vat_amount:                      initial.vat_amount != null ? String(initial.vat_amount) : "",
    vat_rate:                        initial.vat_rate != null ? String(initial.vat_rate) : "21",
    business_use_percent:            initial.business_use_percent != null ? String(initial.business_use_percent) : "80",
    private_or_employer_contribution:initial.private_or_employer_contribution != null ? String(initial.private_or_employer_contribution) : "",
    contribution_basis:              defaultBasis,
    deductible_override:             initial.deductible_business_cost != null ? String(initial.deductible_business_cost) : "",
    claimable_vat_override:          initial.claimable_vat != null ? String(initial.claimable_vat) : "",
    include_in_vat_report:           initial.include_in_vat_report !== false,
    notes:                           initial.notes || "",
    receipt_url:                     initial.receipt_url || "",
  });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const f = (k) => parseFloat(form[k]) || 0;

  // Core allocation calculation — mirrors backend _calc_allocated exactly
  const calcAllocated = () => {
    const contribution = f("private_or_employer_contribution");
    const bizPct       = f("business_use_percent");
    const vatRate      = f("vat_rate");
    const basis        = form.contribution_basis;

    if (contribution > 0 && basis === "incl_vat") {
      const totalInclStored = f("total_amount_incl_vat");
      const totalIncl = totalInclStored > 0
        ? totalInclStored
        : f("amount_excl_vat") + f("vat_amount");
      const remainingIncl  = Math.round((totalIncl - contribution) * 100) / 100;
      // Round businessIncl half-up before deriving claimable/deductible
      const businessIncl   = Math.round((remainingIncl * bizPct / 100 + 1e-9) * 100) / 100;
      const claimableVat   = Math.round(businessIncl * vatRate / (100 + vatRate) * 100) / 100;
      const deductible     = Math.round((businessIncl - claimableVat) * 100) / 100;
      return { remainingIncl: Math.round(remainingIncl * 100) / 100, businessIncl: Math.round(businessIncl * 100) / 100, claimableVat, deductible };
    } else {
      const base       = f("amount_excl_vat") - contribution;
      const deductible = Math.round(base * bizPct / 100 * 100) / 100;
      const claimableVat = Math.round(f("vat_amount") * bizPct / 100 * 100) / 100;
      return { remainingIncl: null, businessIncl: null, claimableVat, deductible };
    }
  };

  const calcDeductible  = () => calcAllocated().deductible;
  const calcClaimableVat = () => calcAllocated().claimableVat;

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      try {
        const ext = file.name.split(".").pop() || "pdf";
        const safeName = `alloc_${form.expense_type}_${form.month}.${ext}`;
        const res = await axios.post(`${API}/uploads/receipt`, {
          file_data: base64, filename: safeName, mime_type: file.type,
          folder: "expense-receipts", invoice_number: "",
        }, { headers: getAuthHeaders() });
        if (res.data.url) setForm(p => ({ ...p, receipt_url: res.data.url }));
        else toast.error("Upload failed");
      } catch { toast.error("Upload failed"); }
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    if (!form.amount_excl_vat) { toast.error("Enter amount excl. VAT"); return; }
    const payload = {
      expense_type:                    form.expense_type,
      month:                           form.month,
      supplier:                        form.supplier || null,
      invoice_reference:               form.invoice_reference || null,
      description:                     form.description || null,
      total_amount_incl_vat:           form.total_amount_incl_vat ? parseFloat(form.total_amount_incl_vat) : null,
      amount_excl_vat:                 parseFloat(form.amount_excl_vat) || 0,
      vat_amount:                      parseFloat(form.vat_amount) || 0,
      vat_rate:                        parseFloat(form.vat_rate) || 21,
      business_use_percent:            parseFloat(form.business_use_percent) || 100,
      private_or_employer_contribution:form.private_or_employer_contribution ? parseFloat(form.private_or_employer_contribution) : null,
      contribution_basis:              form.private_or_employer_contribution ? form.contribution_basis : null,
      deductible_business_cost:        form.deductible_override ? parseFloat(form.deductible_override) : null,
      claimable_vat:                   form.claimable_vat_override ? parseFloat(form.claimable_vat_override) : null,
      include_in_vat_report:           form.include_in_vat_report,
      notes:                           form.notes || null,
      receipt_url:                     form.receipt_url || null,
    };
    onSave(payload);
  };

  const inp = "w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-400";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Type *</Label>
          <select className={inp} value={form.expense_type} onChange={e => setForm(p => ({ ...p, expense_type: e.target.value }))}>
            {EXPENSE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Month *</Label>
          <input type="month" className={inp} value={form.month} onChange={e => setForm(p => ({ ...p, month: e.target.value }))} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Supplier</Label>
          <input className={inp} value={form.supplier} onChange={e => setForm(p => ({ ...p, supplier: e.target.value }))} placeholder="e.g. Odido" />
        </div>
        <div>
          <Label className="text-xs">Invoice Reference</Label>
          <input className={inp} value={form.invoice_reference} onChange={e => setForm(p => ({ ...p, invoice_reference: e.target.value }))} placeholder="Invoice / bill number" />
        </div>
      </div>
      <div>
        <Label className="text-xs">Description</Label>
        <input className={inp} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Odido business phone January 2026" />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-3">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Amounts</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Total incl. VAT (€)</Label>
            <input type="number" step="0.01" className={inp} value={form.total_amount_incl_vat}
              onChange={e => setForm(p => ({ ...p, total_amount_incl_vat: e.target.value }))} placeholder="Optional" />
          </div>
          <div>
            <Label className="text-xs">Amount excl. VAT (€) *</Label>
            <input type="number" step="0.01" className={inp} value={form.amount_excl_vat}
              onChange={e => setForm(p => ({ ...p, amount_excl_vat: e.target.value }))} placeholder="0.00" />
          </div>
          <div>
            <Label className="text-xs">VAT Amount (€)</Label>
            <input type="number" step="0.01" className={inp} value={form.vat_amount}
              onChange={e => setForm(p => ({ ...p, vat_amount: e.target.value }))} placeholder="0.00" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">VAT Rate (%)</Label>
            <input type="number" step="1" className={inp} value={form.vat_rate}
              onChange={e => setForm(p => ({ ...p, vat_rate: e.target.value }))} placeholder="21" />
          </div>
          <div>
            <Label className="text-xs">Private / Employer contribution (€)</Label>
            <input type="number" step="0.01" className={inp} value={form.private_or_employer_contribution}
              onChange={e => setForm(p => ({ ...p, private_or_employer_contribution: e.target.value }))} placeholder="0.00 — leave blank if none" />
          </div>
        </div>
        {form.private_or_employer_contribution && parseFloat(form.private_or_employer_contribution) > 0 && (
          <div>
            <Label className="text-xs">Contribution entered as</Label>
            <select className={inp} value={form.contribution_basis}
              onChange={e => setForm(p => ({ ...p, contribution_basis: e.target.value }))}>
              <option value="incl_vat">Incl. VAT (e.g. €40.00 on invoice = total incl. VAT)</option>
              <option value="excl_vat">Excl. VAT (e.g. €33.06 net, VAT excluded)</option>
            </select>
          </div>
        )}
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-3">
        <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Business-use Allocation</p>
        <div>
          <Label className="text-xs">Business Use %</Label>
          <input type="number" min="1" max="100" step="1" className={inp} value={form.business_use_percent}
            onChange={e => setForm(p => ({ ...p, business_use_percent: e.target.value }))} placeholder="80" />
        </div>
        {form.amount_excl_vat && (() => {
          const calc = calcAllocated();
          const hasContrib = parseFloat(form.private_or_employer_contribution) > 0;
          const basis = form.contribution_basis;
          return (
            <div className="text-xs bg-white rounded border border-purple-100 p-3 space-y-1.5">
              <p className="font-semibold text-purple-700 mb-2">Calculation preview</p>
              {hasContrib && basis === "incl_vat" && (
                <>
                  <div className="flex justify-between text-gray-600">
                    <span>Total incl. VAT</span>
                    <span className="font-mono">€{(f("total_amount_incl_vat") || f("amount_excl_vat") + f("vat_amount")).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>− Contribution (incl. VAT)</span>
                    <span className="font-mono text-red-500">−€{f("private_or_employer_contribution").toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-gray-700 border-t border-gray-100 pt-1">
                    <span>Remaining incl. VAT</span>
                    <span className="font-mono font-semibold">€{calc.remainingIncl?.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>× {f("business_use_percent")}% business use</span>
                    <span className="font-mono">€{calc.businessIncl?.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-blue-700 border-t border-gray-100 pt-1 font-semibold">
                    <span>Deductible cost excl. VAT</span>
                    <span className="font-mono">€{calc.deductible.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-green-700 font-semibold">
                    <span>Claimable VAT ({f("vat_rate")}%)</span>
                    <span className="font-mono">€{calc.claimableVat.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
                  </div>
                </>
              )}
              {(!hasContrib || basis === "excl_vat") && (
                <>
                  {hasContrib && (
                    <div className="flex justify-between text-gray-600">
                      <span>Amount excl. VAT − contribution</span>
                      <span className="font-mono">€{(f("amount_excl_vat") - f("private_or_employer_contribution")).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-blue-700 font-semibold">
                    <span>Deductible cost excl. VAT</span>
                    <span className="font-mono">€{calc.deductible.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-green-700 font-semibold">
                    <span>Claimable VAT</span>
                    <span className="font-mono">€{calc.claimableVat.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
                  </div>
                </>
              )}
            </div>
          );
        })()}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Override deductible cost (€) <span className="font-normal text-gray-400">optional</span></Label>
            <input type="number" step="0.01" className={inp} value={form.deductible_override}
              onChange={e => setForm(p => ({ ...p, deductible_override: e.target.value }))} placeholder={form.amount_excl_vat ? String(calcDeductible()) : "auto"} />
          </div>
          <div>
            <Label className="text-xs">Override claimable VAT (€) <span className="font-normal text-gray-400">optional</span></Label>
            <input type="number" step="0.01" className={inp} value={form.claimable_vat_override}
              onChange={e => setForm(p => ({ ...p, claimable_vat_override: e.target.value }))} placeholder={form.vat_amount ? String(calcClaimableVat()) : "auto"} />
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.include_in_vat_report}
            onChange={e => setForm(p => ({ ...p, include_in_vat_report: e.target.checked }))}
            className="rounded" />
          <span className="text-xs text-purple-800 font-medium">Include in VAT report (adds claimable VAT to input VAT)</span>
        </label>
      </div>

      <div>
        <Label className="text-xs">Notes</Label>
        <input className={inp} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="e.g. 50% of VAT after employer contribution" />
      </div>

      <div>
        <Label className="text-xs">Receipt</Label>
        {form.receipt_url ? (
          <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200 mt-1">
            <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
            <a href={form.receipt_url} target="_blank" rel="noopener noreferrer" className="text-sm text-green-700 truncate flex-1 hover:underline">View receipt</a>
            <button onClick={() => setForm(p => ({ ...p, receipt_url: "" }))} className="text-red-400 hover:text-red-600 shrink-0"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()}
            className="mt-1 w-full border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-purple-400 hover:bg-purple-50 transition-colors">
            <Upload className="w-5 h-5 mx-auto text-gray-400 mb-1" />
            <span className="text-sm text-gray-500">{uploading ? "Uploading…" : "Upload receipt"}</span>
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFile} />
      </div>

      <div className="flex gap-2 pt-2">
        <Button className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" onClick={handleSave}>Save</Button>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

// ── Helpers for ProfitReport ────────────────────────────────────────────────

const CURRENT_MONTH = new Date().toISOString().slice(0, 7);

function currentWeekValue() {
  const d = new Date();
  const jan4 = new Date(d.getFullYear(), 0, 4);
  const dayOfYear = Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 86400000);
  const weekNum = Math.ceil((dayOfYear + jan4.getDay()) / 7);
  return `${d.getFullYear()}-W${String(weekNum).padStart(2, "0")}`;
}

const SOURCE_LABELS = {
  flight_booking: "Flight",
  service_entry:  "Service",
  visa_quote:     "Visa",
};

const SOURCE_COLORS = {
  flight_booking: "bg-blue-100 text-blue-800",
  service_entry:  "bg-purple-100 text-purple-800",
  visa_quote:     "bg-teal-100 text-teal-800",
};

function statusBadge(status) {
  const s = (status || "").toLowerCase();
  if (s === "paid" || s === "betaald") return "bg-green-100 text-green-800";
  if (s === "awaiting_payment" || s === "awaiting payment") return "bg-yellow-100 text-yellow-800";
  if (s === "partially_paid" || s === "partially paid") return "bg-blue-100 text-blue-800";
  return "bg-gray-100 text-gray-600";
}

function fmt(n) {
  return `€${Number(n || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}`;
}

function ProfitReport({ getAuthHeaders }) {
  const [period, setPeriod] = useState("month");
  const [dayVal, setDayVal] = useState(new Date().toISOString().slice(0, 10));
  const [weekVal, setWeekVal] = useState(currentWeekValue());
  const [monthVal, setMonthVal] = useState(CURRENT_MONTH);
  const [quarterQ, setQuarterQ] = useState(`Q${Math.floor(new Date().getMonth() / 3) + 1}`);
  const [quarterY, setQuarterY] = useState(CURRENT_YEAR);
  const [yearVal, setYearVal] = useState(CURRENT_YEAR);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const buildDateParam = () => {
    if (period === "day")     return dayVal;
    if (period === "week")    return weekVal;
    if (period === "month")   return monthVal;
    if (period === "quarter") return `${quarterY}-${quarterQ}`;
    return String(yearVal);
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period, date: buildDateParam() });
      const res = await axios.get(`${API}/accounting/profit-report?${params}`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load profit report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const exportCSV = () => {
    if (!data?.rows?.length) return;
    const headers = ["Invoice #", "Date", "Customer", "Source", "Description",
      "Revenue (€)", "Supplier Cost (€)", "Bank Fee (€)", "VAT (€)",
      "Amount Received (€)", "Gross Profit (€)", "Net Profit (€)", "Payment Status", "Realised"];
    const lines = [headers.join(",")];
    for (const r of data.rows) {
      lines.push([
        r.invoice_number, r.date, `"${(r.customer||"").replace(/"/g,'""')}"`,
        SOURCE_LABELS[r.source_type] || r.source_type,
        `"${(r.description||"").replace(/"/g,'""')}"`,
        r.revenue, r.supplier_cost, r.bank_fee, r.vat_amount,
        r.amount_received, r.gross_profit, r.net_profit,
        r.payment_status, r.realised ? "Yes" : "No",
      ].join(","));
    }
    const s = data.summary;
    lines.push("");
    lines.push(`"Total Revenue","${s.total_revenue}"`);
    lines.push(`"Supplier Cost","${s.supplier_cost}"`);
    lines.push(`"Gross Profit (Realised)","${s.gross_profit_realised}"`);
    lines.push(`"Net Profit (Realised)","${s.net_profit_realised}"`);
    lines.push(`"Outstanding Revenue","${s.outstanding_revenue}"`);
    const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `TBF_Profit_${buildDateParam()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const s = data?.summary;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Period</label>
          <select className="border rounded-md px-2 py-1.5 text-sm"
            value={period} onChange={e => setPeriod(e.target.value)}>
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="quarter">Quarter</option>
            <option value="year">Year</option>
          </select>
        </div>

        {period === "day" && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
            <input type="date" className="border rounded-md px-2 py-1.5 text-sm"
              value={dayVal} onChange={e => setDayVal(e.target.value)} />
          </div>
        )}
        {period === "week" && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Week</label>
            <input type="week" className="border rounded-md px-2 py-1.5 text-sm"
              value={weekVal} onChange={e => setWeekVal(e.target.value)} />
          </div>
        )}
        {period === "month" && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
            <input type="month" className="border rounded-md px-2 py-1.5 text-sm"
              value={monthVal} onChange={e => setMonthVal(e.target.value)} />
          </div>
        )}
        {period === "quarter" && (
          <div className="flex gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Quarter</label>
              <select className="border rounded-md px-2 py-1.5 text-sm"
                value={quarterQ} onChange={e => setQuarterQ(e.target.value)}>
                {["Q1","Q2","Q3","Q4"].map(q => <option key={q} value={q}>{q}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
              <select className="border rounded-md px-2 py-1.5 text-sm"
                value={quarterY} onChange={e => setQuarterY(parseInt(e.target.value))}>
                {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        )}
        {period === "year" && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
            <select className="border rounded-md px-2 py-1.5 text-sm"
              value={yearVal} onChange={e => setYearVal(parseInt(e.target.value))}>
              {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        )}

        <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Apply"}
        </Button>
        {data && (
          <Button size="sm" variant="outline" onClick={exportCSV}>
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
        )}
        {data && (
          <span className="text-xs text-gray-500 ml-auto self-center">
            {data.start} → {data.end} · {data.row_count} transaction{data.row_count !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {loading && <div className="text-center py-8 text-gray-400">Loading…</div>}

      {!loading && data && s && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-3">
            <StatCard title="Total Revenue"     value={s.total_revenue}           icon={TrendingUp}   color="green" />
            <StatCard title="Supplier Cost"     value={s.supplier_cost}           icon={TrendingDown} color="red" />
            <StatCard title="Bank Fees"         value={s.bank_fees}               icon={Receipt}      color="amber"
              sub="Credit card / payment fees" />
            <StatCard title="Gross Profit"      value={s.gross_profit_realised}   icon={Euro}
              color={s.gross_profit_realised >= 0 ? "green" : "red"}
              sub={`Expected: ${fmt(s.gross_profit_expected)}`} />
            <StatCard title="Net Profit"        value={s.net_profit_realised}     icon={BarChart3}
              color={s.net_profit_realised >= 0 ? "purple" : "red"}
              sub={`Expected: ${fmt(s.net_profit_expected)}`} />
            <StatCard title="Outstanding"       value={s.outstanding_revenue}     icon={Clock}        color="amber"
              sub={`${data.row_count - data.realised_count} unpaid item${data.row_count - data.realised_count !== 1 ? "s" : ""}`} />
          </div>
          <p className="text-[10px] text-gray-400 italic">
            Realised = payment received. Expected = all transactions regardless of payment.
            Gross Profit = Amount Received − Supplier Cost. Net Profit = Gross − Fees − VAT.
          </p>

          {/* Breakdown table */}
          {data.rows.length === 0 ? (
            <div className="text-center py-10 text-gray-400">No transactions in this period.</div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-xs border-collapse min-w-[900px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 uppercase text-[10px] tracking-wide">
                    <th className="px-3 py-2 text-left font-semibold">Invoice #</th>
                    <th className="px-3 py-2 text-left font-semibold">Date</th>
                    <th className="px-3 py-2 text-left font-semibold">Customer</th>
                    <th className="px-3 py-2 text-left font-semibold">Source</th>
                    <th className="px-3 py-2 text-right font-semibold">Revenue</th>
                    <th className="px-3 py-2 text-right font-semibold">Cost</th>
                    <th className="px-3 py-2 text-right font-semibold">Fees</th>
                    <th className="px-3 py-2 text-right font-semibold">Gross Profit</th>
                    <th className="px-3 py-2 text-right font-semibold">Net Profit</th>
                    <th className="px-3 py-2 text-left font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i} className={`border-b border-gray-100 hover:bg-gray-50 ${!r.realised ? "opacity-60" : ""}`}>
                      <td className="px-3 py-2 font-mono text-[10px] text-gray-700">{r.invoice_number || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.date || "—"}</td>
                      <td className="px-3 py-2 max-w-[120px] truncate" title={r.customer}>{r.customer || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${SOURCE_COLORS[r.source_type] || "bg-gray-100 text-gray-700"}`}>
                          {SOURCE_LABELS[r.source_type] || r.source_type}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-green-700 whitespace-nowrap">{fmt(r.revenue)}</td>
                      <td className="px-3 py-2 text-right text-red-600 whitespace-nowrap">{r.supplier_cost > 0 ? fmt(r.supplier_cost) : "—"}</td>
                      <td className="px-3 py-2 text-right text-amber-600 whitespace-nowrap">{r.bank_fee > 0 ? fmt(r.bank_fee) : "—"}</td>
                      <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${r.realised ? (r.gross_profit >= 0 ? "text-green-700" : "text-red-600") : "text-gray-400"}`}>
                        {r.realised ? fmt(r.gross_profit) : <span title="Not yet received">—</span>}
                      </td>
                      <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${r.realised ? (r.net_profit >= 0 ? "text-purple-700" : "text-red-600") : "text-gray-400"}`}>
                        {r.realised ? fmt(r.net_profit) : <span title="Not yet received">—</span>}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${statusBadge(r.payment_status)}`}>
                          {r.payment_status || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 font-bold text-gray-800 text-xs border-t-2 border-gray-300">
                    <td colSpan={4} className="px-3 py-2">Totals ({data.realised_count} realised / {data.row_count} total)</td>
                    <td className="px-3 py-2 text-right text-green-700">{fmt(s.total_revenue)}</td>
                    <td className="px-3 py-2 text-right text-red-600">{fmt(s.supplier_cost)}</td>
                    <td className="px-3 py-2 text-right text-amber-600">{fmt(s.bank_fees)}</td>
                    <td className="px-3 py-2 text-right text-green-700">{fmt(s.gross_profit_realised)}</td>
                    <td className="px-3 py-2 text-right text-purple-700">{fmt(s.net_profit_realised)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Recurring Expense Templates ─────────────────────────────────────────────

const FREQ_LABELS = { monthly: "Monthly", quarterly: "Quarterly", annually: "Annually", custom: "Custom" };

function TemplateForm({ initial = {}, onSave, onCancel }) {
  const now = new Date().toISOString().split("T")[0];
  const [f, setF] = useState({
    description: initial.description || "",
    supplier: initial.supplier || "",
    expense_kind: initial.expense_kind || "deductible",
    category: initial.category || "other",
    allocated_expense_type: initial.allocated_expense_type || "mobile_phone",
    amount_excl_vat: initial.amount_excl_vat != null ? String(initial.amount_excl_vat) : "",
    vat_amount: initial.vat_amount != null ? String(initial.vat_amount) : "0",
    vat_rate: initial.vat_rate != null ? String(initial.vat_rate) : "0",
    vat_type: initial.vat_type || "Outside Scope EU VAT",
    currency: initial.currency || "EUR",
    payment_account: initial.payment_account || "Revolut Business",
    business_use_percent: initial.business_use_percent != null ? String(initial.business_use_percent) : "100",
    frequency: initial.frequency || "monthly",
    start_date: initial.start_date || now,
    end_date: initial.end_date || "",
    active: initial.active !== false,
    notes: initial.notes || "",
  });
  const inp = "w-full border rounded-md px-3 py-2 text-sm";

  const handleSubmit = () => {
    if (!f.description.trim()) { toast.error("Description is required"); return; }
    const amtRaw = parseFloat(String(f.amount_excl_vat).replace(",", "."));
    if (isNaN(amtRaw) || f.amount_excl_vat === "") { toast.error("Amount is required"); return; }
    onSave({
      ...f,
      amount_excl_vat: amtRaw,
      vat_amount: parseFloat(String(f.vat_amount).replace(",", ".")) || 0,
      vat_rate: parseFloat(String(f.vat_rate).replace(",", ".")) || 0,
      business_use_percent: parseFloat(f.business_use_percent) || 100,
      end_date: f.end_date || null,
    });
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Description *</Label>
          <Input value={f.description} onChange={e => setF(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Revolut Business fee" />
        </div>
        <div>
          <Label className="text-xs">Supplier</Label>
          <Input value={f.supplier} onChange={e => setF(p => ({ ...p, supplier: e.target.value }))} placeholder="e.g. Revolut" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Expense Type</Label>
          <select className={inp} value={f.expense_kind} onChange={e => setF(p => ({ ...p, expense_kind: e.target.value }))}>
            <option value="deductible">Deductible Expense</option>
            <option value="allocated">Allocated Expense</option>
          </select>
        </div>
        <div>
          {f.expense_kind === "deductible" ? (
            <>
              <Label className="text-xs">Category</Label>
              <select className={inp} value={f.category} onChange={e => setF(p => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </>
          ) : (
            <>
              <Label className="text-xs">Allocated Type</Label>
              <select className={inp} value={f.allocated_expense_type} onChange={e => setF(p => ({ ...p, allocated_expense_type: e.target.value }))}>
                {EXPENSE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Amount excl. VAT (€) *</Label>
          <Input type="number" step="0.01" value={f.amount_excl_vat}
            onChange={e => setF(p => ({ ...p, amount_excl_vat: e.target.value }))} placeholder="0.00" />
        </div>
        <div>
          <Label className="text-xs">VAT Amount (€)</Label>
          <Input type="number" step="0.01" value={f.vat_amount}
            onChange={e => setF(p => ({ ...p, vat_amount: e.target.value }))} placeholder="0.00" />
        </div>
        <div>
          <Label className="text-xs">VAT Type</Label>
          <select className={inp} value={f.vat_type} onChange={e => setF(p => ({ ...p, vat_type: e.target.value }))}>
            {VAT_TYPES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Payment Account</Label>
          <select className={inp} value={f.payment_account} onChange={e => setF(p => ({ ...p, payment_account: e.target.value }))}>
            {PAYMENT_ACCOUNTS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        {f.expense_kind === "allocated" && (
          <div>
            <Label className="text-xs">Business Use %</Label>
            <Input type="number" step="1" min="1" max="100" value={f.business_use_percent}
              onChange={e => setF(p => ({ ...p, business_use_percent: e.target.value }))} placeholder="100" />
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Frequency</Label>
          <select className={inp} value={f.frequency} onChange={e => setF(p => ({ ...p, frequency: e.target.value }))}>
            {Object.entries(FREQ_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Start Date</Label>
          <Input type="date" value={f.start_date} onChange={e => setF(p => ({ ...p, start_date: e.target.value }))} />
        </div>
        <div>
          <Label className="text-xs">End Date (optional)</Label>
          <Input type="date" value={f.end_date} onChange={e => setF(p => ({ ...p, end_date: e.target.value }))} />
        </div>
      </div>
      <div>
        <Label className="text-xs">Notes</Label>
        <Input value={f.notes} onChange={e => setF(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes" />
      </div>
      <div className="flex gap-2 pt-1">
        <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" onClick={handleSubmit}>
          {initial.id ? "Save Changes" : "Create Template"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function GenerateModal({ templates, getAuthHeaders, onClose, onGenerated }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selectedIds, setSelectedIds] = useState(templates.filter(t => t.active).map(t => t.id));
  const [overrides, setOverrides] = useState({});  // { [tmplId]: { amount_excl_vat, vat_amount } }
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const activeTemplates = templates.filter(t => t.active);

  const toggleId = (id) => setSelectedIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const doPreview = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/expense-templates/generate`,
        { template_ids: selectedIds, year, month, preview: true, overrides },
        { headers: getAuthHeaders() }
      );
      setPreview(res.data);
    } catch { toast.error("Preview failed"); }
    setLoading(false);
  };

  const doGenerate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/expense-templates/generate`,
        { template_ids: selectedIds, year, month, preview: false, overrides },
        { headers: getAuthHeaders() }
      );
      const d = res.data;
      toast.success(`Created: ${d.created} · Skipped (duplicates): ${d.skipped_duplicates}${d.errors ? ` · Errors: ${d.errors}` : ""}`);
      onGenerated();
      onClose();
    } catch { toast.error("Generation failed"); }
    setLoading(false);
  };

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-semibold text-gray-800 flex items-center gap-2">
            <Repeat className="w-4 h-4 text-purple-600" /> Generate Recurring Expenses
          </h2>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
        </div>
        <div className="p-4 space-y-4">
          {/* Period selector */}
          <div className="flex gap-3 items-end">
            <div>
              <Label className="text-xs">Year</Label>
              <select className="border rounded-md px-3 py-2 text-sm" value={year} onChange={e => { setYear(parseInt(e.target.value)); setPreview(null); }}>
                {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Month</Label>
              <select className="border rounded-md px-3 py-2 text-sm" value={month} onChange={e => { setMonth(parseInt(e.target.value)); setPreview(null); }}>
                {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
            </div>
            <Button size="sm" variant="outline" onClick={doPreview} disabled={loading || selectedIds.length === 0}>
              {loading ? "..." : "Preview"}
            </Button>
          </div>

          {/* Template selection */}
          <div>
            <Label className="text-xs mb-1 block">Templates to generate ({selectedIds.length} selected)</Label>
            <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
              {activeTemplates.map(t => {
                const sel = selectedIds.includes(t.id);
                const ov = overrides[t.id] || {};
                return (
                  <div key={t.id} className={`p-3 ${sel ? "bg-purple-50" : "bg-white"}`}>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={sel} onChange={() => { toggleId(t.id); setPreview(null); }} />
                      <span className="text-sm font-medium flex-1">{t.description}</span>
                      <span className="text-xs text-gray-500">{FREQ_LABELS[t.frequency]}</span>
                      <Badge className={`text-[10px] ${t.expense_kind === "allocated" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                        {t.expense_kind === "allocated" ? "Allocated" : "Deductible"}
                      </Badge>
                    </div>
                    {sel && (
                      <div className="mt-2 grid grid-cols-2 gap-2 pl-6">
                        <div>
                          <Label className="text-[10px] text-gray-500">Amount excl. VAT (€)</Label>
                          <Input type="number" step="0.01"
                            value={ov.amount_excl_vat != null ? ov.amount_excl_vat : t.amount_excl_vat}
                            onChange={e => { setOverrides(p => ({ ...p, [t.id]: { ...ov, amount_excl_vat: parseFloat(e.target.value) || 0 } })); setPreview(null); }}
                            className="h-7 text-xs" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-gray-500">VAT Amount (€)</Label>
                          <Input type="number" step="0.01"
                            value={ov.vat_amount != null ? ov.vat_amount : t.vat_amount}
                            onChange={e => { setOverrides(p => ({ ...p, [t.id]: { ...ov, vat_amount: parseFloat(e.target.value) || 0 } })); setPreview(null); }}
                            className="h-7 text-xs" />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {activeTemplates.length === 0 && <p className="p-4 text-sm text-gray-400 text-center">No active templates</p>}
            </div>
          </div>

          {/* Preview results */}
          {preview && (
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600 uppercase flex gap-4">
                <span className="text-green-700">Will create: {preview.preview_items.filter(x => x.status === "will_create").length}</span>
                <span className="text-amber-600">Duplicates skipped: {preview.skipped_duplicates}</span>
              </div>
              <div className="divide-y max-h-40 overflow-y-auto">
                {preview.preview_items.map((item, i) => (
                  <div key={i} className="px-3 py-2 flex items-center gap-2 text-sm">
                    {item.status === "will_create"
                      ? <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0" />
                      : <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                    <span className="flex-1">{item.description}</span>
                    <span className="text-xs text-gray-400">{item.period}</span>
                    <span className="text-xs text-gray-500">€{(item.amount_excl_vat || 0).toFixed(2)}</span>
                    {item.status === "duplicate" && <Badge className="text-[10px] bg-amber-100 text-amber-700">Duplicate</Badge>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-end pt-2">
            <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={doGenerate} disabled={loading || selectedIds.length === 0}>
              {loading ? "Generating…" : "Generate Expenses"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RecurringTemplates({ getAuthHeaders }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/expense-templates`, { headers: getAuthHeaders() });
      setTemplates(res.data);
    } catch { toast.error("Failed to load templates"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const saveTemplate = async (data) => {
    try {
      if (editingTemplate) {
        await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/expense-templates/${editingTemplate.id}`, data, { headers: getAuthHeaders() });
        toast.success("Template updated");
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/expense-templates`, data, { headers: getAuthHeaders() });
        toast.success("Template created");
      }
      setShowForm(false); setEditingTemplate(null); load();
    } catch { toast.error("Failed to save template"); }
  };

  const toggleActive = async (t) => {
    try {
      await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/expense-templates/${t.id}`,
        { active: !t.active }, { headers: getAuthHeaders() });
      load();
    } catch { toast.error("Failed to update template"); }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.description}"?`)) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/expense-templates/${t.id}`, { headers: getAuthHeaders() });
      toast.success("Template deleted"); load();
    } catch { toast.error("Failed to delete template"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-gray-800 flex items-center gap-2">
            <Repeat className="w-4 h-4 text-purple-600" /> Recurring Expense Templates
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Define recurring costs and generate them for any month in one click.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setShowGenerate(true)} disabled={templates.filter(t => t.active).length === 0}>
            <Play className="w-4 h-4 mr-1" /> Generate for Month
          </Button>
          <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white"
            onClick={() => { setEditingTemplate(null); setShowForm(true); }}>
            <Plus className="w-4 h-4 mr-1" /> New Template
          </Button>
        </div>
      </div>

      {showForm && (
        <Card className="border-purple-200 bg-purple-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-purple-700">{editingTemplate ? "Edit Template" : "New Recurring Template"}</CardTitle>
          </CardHeader>
          <CardContent>
            <TemplateForm initial={editingTemplate || {}} onSave={saveTemplate}
              onCancel={() => { setShowForm(false); setEditingTemplate(null); }} />
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-400"><Clock className="w-8 h-8 mx-auto mb-2 opacity-30" /><p>Loading…</p></div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Repeat className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No templates yet</p>
          <p className="text-sm mt-1">Create your first recurring expense template above.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Active */}
          {templates.filter(t => t.active).length > 0 && (
            <p className="text-xs font-semibold text-gray-500 uppercase">Active ({templates.filter(t => t.active).length})</p>
          )}
          {templates.filter(t => t.active).map(t => (
            <Card key={t.id} className="border-gray-200">
              <CardContent className="py-3 px-4">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-gray-800">{t.description}</span>
                      {t.supplier && <span className="text-xs text-gray-500">· {t.supplier}</span>}
                      <Badge className={`text-[10px] px-1.5 ${t.expense_kind === "allocated" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                        {t.expense_kind === "allocated" ? "Allocated" : "Deductible"}
                      </Badge>
                      <Badge className="text-[10px] px-1.5 bg-gray-100 text-gray-600">{FREQ_LABELS[t.frequency]}</Badge>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 flex gap-3">
                      <span>€{Number(t.amount_excl_vat).toFixed(2)} excl. VAT</span>
                      {t.vat_amount > 0 && <span>+ €{Number(t.vat_amount).toFixed(2)} VAT</span>}
                      <span>{t.payment_account}</span>
                      {t.start_date && <span>From {t.start_date}</span>}
                      {t.end_date && <span>Until {t.end_date}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button title="Edit" onClick={() => { setEditingTemplate(t); setShowForm(true); }}
                      className="p-1.5 text-gray-400 hover:text-purple-600 rounded">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button title="Pause" onClick={() => toggleActive(t)}
                      className="p-1.5 text-gray-400 hover:text-amber-500 rounded">
                      <Pause className="w-3.5 h-3.5" />
                    </button>
                    <button title="Delete" onClick={() => deleteTemplate(t)}
                      className="p-1.5 text-gray-400 hover:text-red-500 rounded">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Paused */}
          {templates.filter(t => !t.active).length > 0 && (
            <>
              <p className="text-xs font-semibold text-gray-400 uppercase mt-4">Paused ({templates.filter(t => !t.active).length})</p>
              {templates.filter(t => !t.active).map(t => (
                <Card key={t.id} className="border-gray-100 opacity-60">
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-gray-600 line-through">{t.description}</span>
                          <Badge className="text-[10px] px-1.5 bg-gray-100 text-gray-500">Paused</Badge>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">€{Number(t.amount_excl_vat).toFixed(2)} excl. VAT · {FREQ_LABELS[t.frequency]}</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button title="Resume" onClick={() => toggleActive(t)}
                          className="p-1.5 text-gray-400 hover:text-green-600 rounded">
                          <Play className="w-3.5 h-3.5" />
                        </button>
                        <button title="Delete" onClick={() => deleteTemplate(t)}
                          className="p-1.5 text-gray-400 hover:text-red-500 rounded">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </div>
      )}

      {showGenerate && (
        <GenerateModal
          templates={templates}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setShowGenerate(false)}
          onGenerated={load}
        />
      )}
    </div>
  );
}

export default function AccountingPage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [allocatedExpenses, setAllocatedExpenses] = useState([]);
  const [vatReport, setVatReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showAllocForm, setShowAllocForm] = useState(false);
  const [editingAlloc, setEditingAlloc] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [vatQuarter, setVatQuarter] = useState(CURRENT_QUARTER);
  const [vatLimit, setVatLimit] = useState(500);
  const [showDashHelp, setShowDashHelp] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [manualEntries, setManualEntries] = useState([]);

  const [vatCorrections, setVatCorrections] = useState([]);
  const [showIgnoredCorrections, setShowIgnoredCorrections] = useState(false);
  const [showCorrectionForm, setShowCorrectionForm] = useState(false);
  const [editingCorrection, setEditingCorrection] = useState(null); // {id, ...fields}
  const BLANK_CORRECTION = { original_period: "", correction_period: "", correction_type: "Omzet correction", affects_field: "", amount: "", vat_impact: "0", reason: "", notes: "" };
  const [newCorrection, setNewCorrection] = useState(BLANK_CORRECTION);
  const [receiptUrls, setReceiptUrls] = useState({});
  const [collapsedCats, setCollapsedCats] = useState(() => {
    try {
      const stored = localStorage.getItem("tbf-expense-cats");
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  });
  const [expenseSearch, setExpenseSearch] = useState("");

  const viewReceipt = async (exp) => {
    if (exp.receipt_url) {
      window.open(exp.receipt_url, "_blank");
      return;
    }
    if (!exp.receipt_filename) {
      toast.error("No receipt stored");
      return;
    }
    try {
      const res = await axios.get(`${API}/accounting/expenses/${exp.id}/receipt`, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      if (e.response?.status === 404) {
        toast.error("Receipt file missing — delete and re-add this expense with the receipt attached");
      } else {
        toast.error("Could not open receipt");
      }
    }
  };

  const fetchDashboard = async () => {
    try {
      const params = new URLSearchParams({ year: selectedYear });
      if (selectedQuarter) params.append("quarter", selectedQuarter);
      const res = await axios.get(`${API}/accounting/dashboard?${params}`, { headers: getAuthHeaders() });
      setDashboard(res.data);
    } catch { toast.error("Failed to load dashboard"); }
  };

  const fetchExpenses = async () => {
    try {
      const params = new URLSearchParams({ year: selectedYear });
      if (selectedQuarter) params.append("quarter", selectedQuarter);
      const res = await axios.get(`${API}/accounting/expenses?${params}`, { headers: getAuthHeaders() });
      setExpenses(res.data);
    } catch {}
  };

  const fetchAllocatedExpenses = async () => {
    try {
      const params = new URLSearchParams({ year: selectedYear });
      if (selectedQuarter) params.append("quarter", selectedQuarter);
      const res = await axios.get(`${API}/accounting/allocated-expenses?${params}`, { headers: getAuthHeaders() });
      setAllocatedExpenses(res.data);
    } catch {}
  };

  const saveAllocatedExpense = async (payload) => {
    try {
      if (editingAlloc) {
        await axios.patch(`${API}/accounting/allocated-expenses/${editingAlloc.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Updated ✓");
      } else {
        await axios.post(`${API}/accounting/allocated-expenses`, payload, { headers: getAuthHeaders() });
        toast.success("Saved ✓");
      }
      setShowAllocForm(false);
      setEditingAlloc(null);
      await fetchAllocatedExpenses();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const deleteAllocatedExpense = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await axios.delete(`${API}/accounting/allocated-expenses/${id}`, { headers: getAuthHeaders() });
      toast.success("Deleted");
      await fetchAllocatedExpenses();
    } catch { toast.error("Delete failed"); }
  };

  const handleReceiptUploaded = (invoiceNumber, url) => {
    setReceiptUrls(prev => ({ ...prev, [invoiceNumber]: url }));
    setVatReport(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        revenue_rows: prev.revenue_rows.map(r =>
          r.invoice_number === invoiceNumber ? { ...r, supplier_receipt_url: url } : r
        )
      };
    });
  };

  const fetchVatReport = async () => {
    try {
      const params = new URLSearchParams({ year: selectedYear, limit: vatLimit });
      if (vatQuarter) params.append("quarter", vatQuarter);
      const res = await axios.get(`${API}/accounting/vat-report?${params}`, { headers: getAuthHeaders() });
      setVatReport(res.data);
    } catch { toast.error("Failed to load VAT report"); }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchExpenses(), fetchAllocatedExpenses()]);
      setLoading(false);
    };
    load();
  }, [selectedQuarter, selectedYear]);

  // Keep vatQuarter in sync with the top-level quarter selector
  useEffect(() => {
    setVatQuarter(selectedQuarter);
  }, [selectedQuarter]);

  useEffect(() => {
    if (activeTab === "allocated") fetchAllocatedExpenses();
  }, [activeTab, selectedQuarter, selectedYear]);

  useEffect(() => {
    if (activeTab === "vat") fetchVatReport();
  }, [activeTab, vatQuarter, selectedYear, vatLimit]);

  const fetchManualEntries = async () => {
    try {
      const params = new URLSearchParams({ year: selectedYear });
      if (selectedQuarter) params.append("quarter", selectedQuarter);
      const res = await axios.get(`${API}/accounting/service-entries?${params}`, { headers: getAuthHeaders() });
      setManualEntries(res.data);
    } catch { toast.error("Failed to load manual entries"); }
  };

  useEffect(() => {
    if (activeTab === "manual") fetchManualEntries();
  }, [activeTab, selectedQuarter, selectedYear]);

  const fetchVatCorrections = async () => {
    try {
      const res = await axios.get(`${API}/accounting/vat-corrections`, { headers: getAuthHeaders() });
      setVatCorrections(res.data);
    } catch {}
  };

  const updateCorrectionStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/accounting/vat-corrections/${id}`, { status }, { headers: getAuthHeaders() });
      await fetchVatCorrections();
      toast.success(`Correction marked as ${status}`);
    } catch { toast.error("Failed to update correction"); }
  };

  const deleteCorrection = async (id) => {
    if (!window.confirm("Delete this correction reminder?")) return;
    try {
      await axios.delete(`${API}/accounting/vat-corrections/${id}`, { headers: getAuthHeaders() });
      await fetchVatCorrections();
      toast.success("Correction deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const saveCorrection = async () => {
    if (!newCorrection.original_period || !newCorrection.correction_period || !newCorrection.amount) {
      toast.error("Fill in original period, correction period and amount"); return;
    }
    try {
      await axios.post(`${API}/accounting/vat-corrections`, {
        ...newCorrection,
        affects_field: newCorrection.affects_field || null,
        amount: parseFloat(newCorrection.amount) || 0,
        vat_impact: parseFloat(newCorrection.vat_impact) || 0,
      }, { headers: getAuthHeaders() });
      await fetchVatCorrections();
      setShowCorrectionForm(false);
      setNewCorrection(BLANK_CORRECTION);
      toast.success("Correction saved");
    } catch { toast.error("Failed to save correction"); }
  };

  const saveEditedCorrection = async () => {
    if (!editingCorrection) return;
    try {
      await axios.patch(`${API}/accounting/vat-corrections/${editingCorrection.id}`, {
        original_period:  editingCorrection.original_period,
        correction_period: editingCorrection.correction_period,
        correction_type:  editingCorrection.correction_type,
        affects_field:    editingCorrection.affects_field || null,
        amount:           parseFloat(editingCorrection.amount) || 0,
        vat_impact:       parseFloat(editingCorrection.vat_impact) || 0,
        reason:           editingCorrection.reason,
        notes:            editingCorrection.notes,
      }, { headers: getAuthHeaders() });
      await fetchVatCorrections();
      setEditingCorrection(null);
      toast.success("Correction updated");
    } catch { toast.error("Failed to update correction"); }
  };

  useEffect(() => {
    if (activeTab === "vat") fetchVatCorrections();
  }, [activeTab]);

  const saveExpense = async (formData) => {
    try {
      if (editingExpense) {
        await axios.patch(`${API}/accounting/expenses/${editingExpense.id}`, formData, { headers: getAuthHeaders() });
        toast.success("Expense updated");
      } else {
        await axios.post(`${API}/accounting/expenses`, formData, { headers: getAuthHeaders() });
        toast.success("Expense saved");
      }
      setShowExpenseForm(false);
      setEditingExpense(null);
      await Promise.all([fetchDashboard(), fetchExpenses()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save expense");
    }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await axios.delete(`${API}/accounting/expenses/${id}`, { headers: getAuthHeaders() });
      toast.success("Deleted");
      await Promise.all([fetchDashboard(), fetchExpenses()]);
    } catch { toast.error("Failed to delete"); }
  };

  const downloadCSV = async () => {
    try {
      const params = new URLSearchParams({ year: selectedYear });
      if (selectedQuarter) params.append("quarter", selectedQuarter);
      const res = await axios.get(`${API}/accounting/export-csv?${params}`,
        { headers: getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `TBF_Boekhouding_${selectedQuarter || "all"}_${selectedYear}.csv`;
      a.click();
    } catch { toast.error("Export failed"); }
  };

  const [customCategories, setCustomCategories] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tbf-custom-cats") || "[]"); } catch { return []; }
  });

  const addCustomCategory = (label) => {
    const value = label.toLowerCase().trim().replace(/\s+/g, "_");
    if (!CATEGORIES.find(c => c.value === value) && !customCategories.find(c => c.value === value)) {
      const updated = [...customCategories, { value, label: label.trim() }];
      setCustomCategories(updated);
      try { localStorage.setItem("tbf-custom-cats", JSON.stringify(updated)); } catch {}
    }
  };

  const catLabel = (cat) => CATEGORIES.find(c => c.value === cat)?.label || customCategories.find(c => c.value === cat)?.label || cat;

  const toggleCategory = (cat) => {
    setCollapsedCats(prev => {
      const next = { ...prev, [cat]: !prev[cat] };
      try { localStorage.setItem("tbf-expense-cats", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const tabs = [
    { key: "dashboard",  label: "Dashboard" },
    { key: "expenses",   label: `Expenses (${expenses.length})` },
    { key: "allocated",  label: `Allocated (${allocatedExpenses.length})` },
    { key: "vat",        label: "VAT Report" },
    { key: "manual",     label: `Manual Entries${manualEntries.length ? ` (${manualEntries.length})` : ""}` },
    { key: "profit",     label: "Profit Report" },
    { key: "recurring",  label: "Recurring" },
  ];

  return (
    <DashboardLayout>
    <div className="max-w-7xl mx-auto p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Bookkeeping</h1>
          <p className="text-sm text-gray-500">The Better Fare — Financial Overview</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={selectedQuarter} onValueChange={v => setSelectedQuarter(v)}>
            <SelectTrigger className="w-32 text-sm h-8">
              <SelectValue placeholder="All Quarters" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">All Quarters</SelectItem>
              {QUARTERS.map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(selectedYear)} onValueChange={v => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-24 text-sm h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={downloadCSV}>
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.key ? "border-purple-600 text-purple-700" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-center py-8 text-gray-400">Loading...</div>}

      {/* DASHBOARD */}
      {!loading && activeTab === "dashboard" && dashboard && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => setShowDashHelp(true)}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-purple-700 border border-gray-200 hover:border-purple-300 rounded-md px-2.5 py-1.5 transition-colors">
              <HelpCircle className="w-3.5 h-3.5" />
              How are these totals calculated?
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <StatCard title="Total Revenue"  value={dashboard.total_revenue}
              sub={`${dashboard.bookings_count} bookings${dashboard.service_entries_count ? ` + ${dashboard.service_entries_count} manual` : ""}`}
              icon={TrendingUp}  color="green" />
            <StatCard title="Supplier Cost"  value={dashboard.total_supplier_cost}  icon={TrendingDown} color="red" />
            <StatCard title="Gross Profit"   value={dashboard.gross_profit}         sub="Revenue - Cost" icon={Euro} color={dashboard.gross_profit >= 0 ? "green" : "red"} />
            <StatCard title="Total Expenses" value={dashboard.total_expenses}       sub="Operating costs" icon={Receipt} color="amber" />
            <StatCard title="Net Profit"     value={dashboard.net_profit}           sub="Gross - Expenses" icon={Euro} color={dashboard.net_profit >= 0 ? "purple" : "red"} />
            <StatCard title="Outstanding"    value={dashboard.outstanding_receivables} sub="Not yet received" icon={Clock} color="amber" />
          </div>
          <p className="text-[10px] text-gray-400 italic">
            Dashboard includes bookings and manual service/refund/correction entries.
          </p>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-gray-700">BTW (VAT) Summary</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div className="p-2 bg-gray-50 rounded">
                  <p className="text-xs text-gray-500">0% VAT (Flights)</p>
                  <p className="font-bold">€{Number(dashboard.vat_breakdown?.["0_percent"]?.revenue || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="p-2 bg-gray-50 rounded">
                  <p className="text-xs text-gray-500">Outside Scope EU</p>
                  <p className="font-bold">€{Number(dashboard.vat_breakdown?.outside_scope?.revenue || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="p-2 bg-gray-50 rounded">
                  <p className="text-xs text-gray-500">21% VAT Collected</p>
                  <p className="font-bold text-amber-600">€{Number(dashboard.vat_breakdown?.["21_percent"]?.vat_due || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="p-2 bg-purple-50 rounded">
                  <p className="text-xs text-gray-500">Input VAT Reclaimable</p>
                  <p className="font-bold text-purple-600">€{Number(dashboard.vat_breakdown?.reclaimable_input_vat || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {Object.keys(dashboard.by_quarter || {}).length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-gray-700">By Quarter</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(dashboard.by_quarter).sort().map(([q, d]) => (
                    <div key={q} className="flex items-center gap-3 text-sm">
                      <Badge className="bg-purple-100 text-purple-700 w-10 justify-center">{q}</Badge>
                      <div className="flex-1 grid grid-cols-3 gap-2 text-xs">
                        <span><span className="text-gray-400">Rev: </span><span className="font-medium text-green-700">€{Number(d.revenue || 0).toLocaleString("nl-NL", { minimumFractionDigits: 0 })}</span></span>
                        <span><span className="text-gray-400">Profit: </span><span className="font-medium text-blue-700">€{Number(d.gross_profit || 0).toLocaleString("nl-NL", { minimumFractionDigits: 0 })}</span></span>
                        <span><span className="text-gray-400">Exp: </span><span className="font-medium text-red-600">€{Number(d.expenses || 0).toLocaleString("nl-NL", { minimumFractionDigits: 0 })}</span></span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {Object.keys(dashboard.expense_by_category || {}).length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-gray-700">Expenses by Category</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(dashboard.expense_by_category).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                    <div key={cat} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{catLabel(cat)}</span>
                      <span className="font-medium text-red-600">€{Number(amt).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* EXPENSES */}
      {!loading && activeTab === "expenses" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-800">Business Expenses</h2>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowImport(true)}>
                <FileUp className="w-4 h-4 mr-1" /> Import from Excel
              </Button>
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white"
                onClick={() => { setEditingExpense(null); setShowExpenseForm(true); }}>
                <Plus className="w-4 h-4 mr-1" /> Add Expense
              </Button>
            </div>
          </div>
          {showExpenseForm && (
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-purple-700">{editingExpense ? "Edit Expense" : "New Expense"}</CardTitle>
              </CardHeader>
              <CardContent>
                <ExpenseForm initial={editingExpense || {}} onSave={saveExpense}
                  onCancel={() => { setShowExpenseForm(false); setEditingExpense(null); }}
                  extraCategories={customCategories} onAddCategory={addCustomCategory} />
              </CardContent>
            </Card>
          )}
          {/* Search */}
          {expenses.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search expenses..."
                value={expenseSearch}
                onChange={e => setExpenseSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-400"
              />
            </div>
          )}
          {/* Grouped, collapsible categories */}
          {(() => {
            const searchLower = expenseSearch.toLowerCase().trim();
            const visible = searchLower
              ? expenses.filter(e =>
                  (e.description || "").toLowerCase().includes(searchLower) ||
                  (e.notes || "").toLowerCase().includes(searchLower) ||
                  catLabel(e.category || "other").toLowerCase().includes(searchLower)
                )
              : expenses;
            if (visible.length === 0) {
              return (
                <div className="text-center py-8 text-gray-400">
                  <Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>{searchLower ? "No expenses match your search." : "No expenses yet."}</p>
                </div>
              );
            }
            const grouped = visible.reduce((acc, exp) => {
              const cat = exp.category || "other";
              if (!acc[cat]) acc[cat] = [];
              acc[cat].push(exp);
              return acc;
            }, {});
            const categoryOrder = ["credit_card_fee","bank_fee","software","development","travel","visa_cost","marketing","office","meal","other"];
            const sortedCats = Object.keys(grouped).sort((a, b) => {
              const ai = categoryOrder.indexOf(a);
              const bi = categoryOrder.indexOf(b);
              return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
            });
            return (
              <>
                <div className="space-y-2">
                  {sortedCats.map(cat => {
                  const catExpenses = grouped[cat];
                  const catTotal = catExpenses.reduce((sum, e) => sum + getDeductibleAmount(e), 0);
                  const isCollapsed = !!collapsedCats[cat];
                  return (
                    <div key={cat} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Category header — click to toggle */}
                      <button
                        onClick={() => toggleCategory(cat)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {isCollapsed
                            ? <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                            : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                          }
                          <span className="text-sm font-semibold text-gray-700 truncate">{catLabel(cat)}</span>
                          <span className="text-xs text-gray-400 shrink-0 hidden sm:inline">
                            {catExpenses.length} {catExpenses.length === 1 ? "expense" : "expenses"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          <span className="text-xs text-gray-400 sm:hidden">{catExpenses.length}</span>
                          <span className="text-sm font-bold text-red-600">
                            €{catTotal.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      </button>
                      {/* Expense rows — hidden when collapsed */}
                      {!isCollapsed && (
                        <div className="divide-y divide-gray-100">
                          {catExpenses.map(exp => (
                            <div key={exp.id} className="px-4 py-3 hover:bg-purple-50/30 transition-colors">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-sm text-gray-800">{exp.description}</span>
                                    <Badge className="text-[10px] px-1 bg-gray-100 text-gray-600">{exp.quarter || "—"}</Badge>
                                    {exp.receipt_filename && (
                                      <Badge className="text-[10px] px-1 bg-green-100 text-green-700">receipt</Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 flex-wrap">
                                    <span>{exp.date}</span>
                                    <span>·</span>
                                    <span>{exp.payment_account}</span>
                                    {exp.vat_type && <><span>·</span><span>{exp.vat_type}</span></>}
                                  </div>
                                  {exp.notes && <p className="text-xs text-gray-400 mt-0.5 italic">{exp.notes}</p>}
                                </div>
                                <div className="text-right shrink-0">
                                  {exp.business_use_percent != null && Number(exp.business_use_percent) < 100 ? (
                                    <>
                                      <p className="font-bold text-red-600 text-sm">
                                        €{getDeductibleAmount(exp).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}
                                      </p>
                                      <p className="text-xs text-gray-400">({exp.business_use_percent}% of €{getNetAmount(exp).toLocaleString("nl-NL", { minimumFractionDigits: 2 })})</p>
                                    </>
                                  ) : (
                                    <p className="font-bold text-red-600 text-sm">
                                      €{getNetAmount(exp).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}
                                    </p>
                                  )}
                                  {exp.vat_amount > 0 && <p className="text-xs text-amber-600">BTW: €{exp.vat_amount}</p>}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 mt-1.5">
                                {exp.receipt_filename && (
                                  <button
                                    onClick={() => viewReceipt(exp)}
                                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                  >
                                    <Eye className="w-3 h-3" /> View Receipt
                                  </button>
                                )}
                                <button
                                  onClick={() => { setEditingExpense(exp); setShowExpenseForm(true); }}
                                  className="text-xs text-purple-600 hover:underline ml-auto"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => deleteExpense(exp.id)}
                                  className="text-xs text-red-500 hover:underline"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                  })}
                </div>
                {/* Total expenses summary */}
                <div className="flex items-center justify-between px-4 py-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div>
                    <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Total Expenses</p>
                    <p className="text-xs text-purple-400 mt-0.5">
                      {visible.length} {visible.length === 1 ? "expense" : "expenses"}
                    </p>
                  </div>
                  <p className="text-xl font-bold text-red-600">
                    €{visible.reduce((sum, e) => sum + getDeductibleAmount(e), 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* ALLOCATED EXPENSES */}
      {activeTab === "allocated" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="font-semibold text-gray-800">Allocated / Business-use Expenses</h2>
              <p className="text-xs text-gray-500 mt-0.5">Partial-business expenses such as phone, internet, home office — enter the full bill and set a business-use percentage.</p>
            </div>
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => { setEditingAlloc(null); setShowAllocForm(true); }}>
              <Plus className="w-4 h-4 mr-1" /> Add Entry
            </Button>
          </div>

          {showAllocForm && (
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-purple-700">{editingAlloc ? "Edit Entry" : "New Allocated Expense"}</CardTitle>
              </CardHeader>
              <CardContent>
                <AllocatedExpenseForm
                  initial={editingAlloc || {}}
                  onSave={saveAllocatedExpense}
                  onCancel={() => { setShowAllocForm(false); setEditingAlloc(null); }}
                />
              </CardContent>
            </Card>
          )}

          {/* Quarterly summary */}
          {allocatedExpenses.length > 0 && (() => {
            const totalDeductible  = allocatedExpenses.reduce((s, e) => s + (e._deductible_business_cost || 0), 0);
            const totalClaimableVat = allocatedExpenses.reduce((s, e) => s + (e._claimable_vat || 0), 0);
            const byType = {};
            allocatedExpenses.forEach(e => {
              if (!byType[e.expense_type]) byType[e.expense_type] = { deductible: 0, vat: 0 };
              byType[e.expense_type].deductible += e._deductible_business_cost || 0;
              byType[e.expense_type].vat        += e._claimable_vat || 0;
            });
            return (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg border bg-blue-50 border-blue-200">
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Total Deductible Cost</p>
                    <p className="text-xl font-bold text-blue-700 mt-0.5">€{totalDeductible.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{allocatedExpenses.length} entries · {selectedQuarter || "All quarters"} {selectedYear}</p>
                  </div>
                  <div className="p-3 rounded-lg border bg-green-50 border-green-200">
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Total Claimable VAT</p>
                    <p className="text-xl font-bold text-green-700 mt-0.5">€{totalClaimableVat.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Input VAT reclaimable from Belastingdienst</p>
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Breakdown by type</p>
                  <div className="space-y-1">
                    {Object.entries(byType).map(([type, vals]) => (
                      <div key={type} className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">{expTypeLabel(type)}</span>
                        <span className="text-gray-500">
                          Deductible: <span className="font-semibold text-blue-700">€{vals.deductible.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
                          <span className="mx-2">·</span>
                          VAT: <span className="font-semibold text-green-700">€{vals.vat.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Entries list */}
          {allocatedExpenses.length === 0 && !showAllocForm && (
            <div className="text-center py-10 text-gray-400">
              <Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No allocated expenses yet.</p>
              <p className="text-xs mt-1">Add phone, internet, home office entries here.</p>
            </div>
          )}
          {allocatedExpenses.length > 0 && (
            <div className="overflow-x-auto rounded border border-gray-200">
              <table className="w-full text-xs border-collapse min-w-[900px]">
                <thead>
                  <tr className="bg-purple-50 text-purple-700 text-left">
                    <th className="px-3 py-2 font-semibold border-b border-gray-200">Month</th>
                    <th className="px-3 py-2 font-semibold border-b border-gray-200">Type</th>
                    <th className="px-3 py-2 font-semibold border-b border-gray-200">Supplier</th>
                    <th className="px-3 py-2 font-semibold border-b border-gray-200">Description</th>
                    <th className="px-3 py-2 font-semibold border-b border-gray-200 text-right">Excl. VAT</th>
                    <th className="px-3 py-2 font-semibold border-b border-gray-200 text-right">VAT</th>
                    <th className="px-3 py-2 font-semibold border-b border-gray-200 text-center">Bus.%</th>
                    <th className="px-3 py-2 font-semibold border-b border-gray-200 text-right">Deductible</th>
                    <th className="px-3 py-2 font-semibold border-b border-gray-200 text-right">Claimable VAT</th>
                    <th className="px-3 py-2 font-semibold border-b border-gray-200 text-center">In VAT rep.</th>
                    <th className="px-3 py-2 font-semibold border-b border-gray-200">Receipt</th>
                    <th className="px-3 py-2 font-semibold border-b border-gray-200"></th>
                  </tr>
                </thead>
                <tbody>
                  {allocatedExpenses.map((e, i) => (
                    <tr key={e.id} className={i % 2 === 0 ? "bg-white hover:bg-yellow-50" : "bg-gray-50 hover:bg-yellow-50"}>
                      <td className="px-3 py-2 border-b border-gray-100 font-mono whitespace-nowrap">{e.month}</td>
                      <td className="px-3 py-2 border-b border-gray-100 whitespace-nowrap">
                        <Badge className="text-[10px] px-1.5 bg-purple-100 text-purple-700">{expTypeLabel(e.expense_type)}</Badge>
                      </td>
                      <td className="px-3 py-2 border-b border-gray-100 text-gray-700">{e.supplier || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 border-b border-gray-100 text-gray-600 max-w-[180px] truncate" title={e.description || ""}>{e.description || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 border-b border-gray-100 text-right font-mono">€{Number(e.amount_excl_vat || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2 border-b border-gray-100 text-right font-mono text-amber-700">€{Number(e.vat_amount || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2 border-b border-gray-100 text-center font-semibold text-purple-700">{e.business_use_percent}%</td>
                      <td className="px-3 py-2 border-b border-gray-100 text-right font-bold text-blue-700">€{Number(e._deductible_business_cost || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2 border-b border-gray-100 text-right font-bold text-green-700">€{Number(e._claimable_vat || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2 border-b border-gray-100 text-center">
                        {e.include_in_vat_report
                          ? <span className="text-green-600 font-semibold">✓</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2 border-b border-gray-100">
                        {e.receipt_url
                          ? <a href={e.receipt_url} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600">
                              <Eye className="w-3 h-3" />
                            </a>
                          : <span className="text-gray-300 text-[10px]">none</span>}
                      </td>
                      <td className="px-3 py-2 border-b border-gray-100">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => { setEditingAlloc(e); setShowAllocForm(true); }}
                            className="text-purple-500 hover:text-purple-700 text-[10px] font-medium">Edit</button>
                          <button onClick={() => deleteAllocatedExpense(e.id)}
                            className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-purple-50 font-bold text-purple-800 text-xs">
                    <td colSpan={4} className="px-3 py-2">Totals</td>
                    <td className="px-3 py-2 text-right">€{allocatedExpenses.reduce((s, e) => s + (e.amount_excl_vat || 0), 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</td>
                    <td className="px-3 py-2 text-right text-amber-700">€{allocatedExpenses.reduce((s, e) => s + (e.vat_amount || 0), 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</td>
                    <td />
                    <td className="px-3 py-2 text-right text-blue-700">€{allocatedExpenses.reduce((s, e) => s + (e._deductible_business_cost || 0), 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</td>
                    <td className="px-3 py-2 text-right text-green-700">€{allocatedExpenses.reduce((s, e) => s + (e._claimable_vat || 0), 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* VAT REPORT */}
      {activeTab === "vat" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-gray-700 bg-gray-100 rounded-md px-3 py-2">
              {vatQuarter ? `${vatQuarter} ${selectedYear}` : `All Quarters ${selectedYear}`}
            </span>
            <Button size="sm" variant="outline" onClick={fetchVatReport}>Refresh</Button>
            <Button size="sm" variant="outline" onClick={downloadCSV}>
              <Download className="w-4 h-4 mr-1" /> Export {vatQuarter || "All"}
            </Button>
            <Button size="sm" variant="outline" onClick={async () => {
              try {
                const params = new URLSearchParams({ year: selectedYear });
                if (vatQuarter) params.append("quarter", vatQuarter);
                const res = await axios.get(`${API}/accounting/vat-report/export-excel?${params}`,
                  { headers: getAuthHeaders(), responseType: "blob" });
                const label = vatQuarter ? `${vatQuarter.replace(" ","")}` : "All";
                const url = URL.createObjectURL(new Blob([res.data], {
                  type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `BTW_Dossier_TheBetterFare_${label}_${selectedYear}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
              } catch { toast.error("Excel export failed"); }
            }}>
              <Download className="w-4 h-4 mr-1" /> Generate BTW Excel
            </Button>

          </div>

          {/* VAT Corrections Banner */}
          {(() => {
            const pendingCorr = vatCorrections.filter(c => c.status === "pending" && (
              !vatQuarter || c.correction_period === `${vatQuarter} ${selectedYear}`
            ));
            if (!pendingCorr.length) return null;
            return (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <span className="text-sm font-semibold text-amber-800">
                    {pendingCorr.length} openstaande BTW correctie{pendingCorr.length > 1 ? "s" : ""} voor {vatQuarter || "dit jaar"}
                  </span>
                </div>
                {pendingCorr.map(c => (
                  <div key={c.id} className="flex items-start justify-between gap-2 bg-white rounded p-2 border border-amber-200">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-amber-900">
                        {c.original_period} → {c.correction_period} &nbsp;|&nbsp; {c.correction_type} &nbsp;|&nbsp;
                        <span className="font-bold">€{Number(c.amount).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
                        {c.vat_impact !== 0 && <span className="ml-1 text-red-700"> (+€{Number(c.vat_impact).toLocaleString("nl-NL", { minimumFractionDigits: 2 })} BTW)</span>}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5 truncate">{c.reason}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => updateCorrectionStatus(c.id, "applied")}
                        className="text-xs bg-green-100 hover:bg-green-200 text-green-800 px-2 py-0.5 rounded border border-green-300">
                        Verwerkt
                      </button>
                      <button onClick={() => updateCorrectionStatus(c.id, "ignored")}
                        className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-2 py-0.5 rounded border border-gray-300">
                        Negeren
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* VAT Corrections Management */}
          {(() => {
            const activeCorrections  = vatCorrections.filter(c => c.status !== "ignored");
            const ignoredCorrections = vatCorrections.filter(c => c.status === "ignored");
            const pendingCount = activeCorrections.filter(c => c.status === "pending").length;
            const showPanel = activeCorrections.length > 0 || ignoredCorrections.length > 0;

            const FIELD_OPTS = ["", "1a", "1b", "1e", "5a", "5b"];

            const CorrectionRow = ({ c, showActions = true }) => {
              const isEditing = editingCorrection?.id === c.id;
              if (isEditing) {
                const ec = editingCorrection;
                const setEc = (patch) => setEditingCorrection(p => ({ ...p, ...patch }));
                return (
                  <div className="rounded p-2 border border-blue-200 bg-blue-50 text-xs space-y-2">
                    <div className="grid grid-cols-2 gap-1.5">
                      <div>
                        <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Oorspronkelijke periode</label>
                        <input className="w-full h-6 border border-gray-300 rounded px-1.5 text-xs" value={ec.original_period}
                          onChange={e => setEc({ original_period: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Ingediend in periode</label>
                        <input className="w-full h-6 border border-gray-300 rounded px-1.5 text-xs" value={ec.correction_period}
                          onChange={e => setEc({ correction_period: e.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                      <div>
                        <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Type</label>
                        <input className="w-full h-6 border border-gray-300 rounded px-1.5 text-xs" value={ec.correction_type}
                          onChange={e => setEc({ correction_type: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Veld</label>
                        <select className="w-full h-6 border border-gray-300 rounded px-1 text-xs" value={ec.affects_field || ""}
                          onChange={e => setEc({ affects_field: e.target.value })}>
                          {FIELD_OPTS.map(f => <option key={f} value={f}>{f || "—"}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Bedrag (€)</label>
                        <input className="w-full h-6 border border-gray-300 rounded px-1.5 text-xs" type="number" step="0.01" value={ec.amount}
                          onChange={e => setEc({ amount: e.target.value })} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Reden</label>
                      <input className="w-full h-6 border border-gray-300 rounded px-1.5 text-xs" value={ec.reason}
                        onChange={e => setEc({ reason: e.target.value })} />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Notities</label>
                      <input className="w-full h-6 border border-gray-300 rounded px-1.5 text-xs" value={ec.notes || ""}
                        onChange={e => setEc({ notes: e.target.value })} />
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={saveEditedCorrection}
                        className="text-[10px] bg-blue-600 hover:bg-blue-700 text-white px-2 py-0.5 rounded">Opslaan</button>
                      <button onClick={() => setEditingCorrection(null)}
                        className="text-[10px] bg-gray-100 hover:bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Annuleren</button>
                    </div>
                  </div>
                );
              }

              return (
                <div className={`flex items-start justify-between gap-2 rounded p-2 border text-xs ${
                  c.status === "pending" ? "bg-amber-50 border-amber-200" :
                  c.status === "applied" ? "bg-green-50 border-green-200" :
                  "bg-gray-50 border-gray-200 opacity-60"
                }`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1">
                      <span className="font-semibold">{c.original_period} → {c.correction_period}</span>
                      <span className="text-gray-400">|</span>
                      <span>{c.correction_type}</span>
                      {c.affects_field && (
                        <span className="px-1 py-0 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold">{c.affects_field}</span>
                      )}
                      <span className="text-gray-400">|</span>
                      <span className="font-bold">€{Number(c.amount).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                        c.status === "pending" ? "bg-amber-200 text-amber-800" :
                        c.status === "applied" ? "bg-green-200 text-green-800" :
                        "bg-gray-200 text-gray-500"
                      }`}>{c.status.toUpperCase()}</span>
                    </div>
                    {c.reason && <p className="text-gray-500 mt-0.5 truncate">{c.reason}</p>}
                    {c.notes && <p className="text-gray-400 mt-0 truncate italic">{c.notes}</p>}
                  </div>
                  {showActions && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => setEditingCorrection({ ...c, amount: String(c.amount), vat_impact: String(c.vat_impact ?? 0) })}
                        className="text-[10px] bg-blue-50 hover:bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded" title="Bewerken">✎</button>
                      {c.status !== "applied" && (
                        <button onClick={() => updateCorrectionStatus(c.id, "applied")}
                          className="text-[10px] bg-green-100 hover:bg-green-200 text-green-700 px-1.5 py-0.5 rounded" title="Mark applied">✓</button>
                      )}
                      {c.status !== "pending" && (
                        <button onClick={() => updateCorrectionStatus(c.id, "pending")}
                          className="text-[10px] bg-amber-100 hover:bg-amber-200 text-amber-700 px-1.5 py-0.5 rounded" title="Mark pending">↩</button>
                      )}
                      {c.status !== "ignored" && (
                        <button onClick={() => updateCorrectionStatus(c.id, "ignored")}
                          className="text-[10px] bg-gray-100 hover:bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded" title="Archiveren">⊘</button>
                      )}
                      <button onClick={() => deleteCorrection(c.id)}
                        className="text-[10px] bg-red-50 hover:bg-red-100 text-red-600 px-1.5 py-0.5 rounded" title="Verwijderen">✕</button>
                    </div>
                  )}
                </div>
              );
            };

            return showPanel ? (
              <details className="border border-gray-200 rounded-lg">
                <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-50 flex items-center gap-2">
                  <span>BTW Correcties ({activeCorrections.length})</span>
                  {pendingCount > 0 && (
                    <span className="text-amber-600">• {pendingCount} pending</span>
                  )}
                </summary>
                <div className="p-3 space-y-2">
                  {activeCorrections.length === 0 && (
                    <p className="text-xs text-gray-400">Geen actieve correcties.</p>
                  )}
                  {activeCorrections.map(c => <CorrectionRow key={c.id} c={c} showActions={true} />)}

                  {/* Ignored / archived toggle */}
                  {ignoredCorrections.length > 0 && (
                    <div className="pt-1">
                      <button
                        onClick={() => setShowIgnoredCorrections(v => !v)}
                        className="text-[11px] text-gray-400 hover:text-gray-600 underline"
                      >
                        {showIgnoredCorrections
                          ? "Verberg gearchiveerde correcties"
                          : `Toon gearchiveerde / genegeerde correcties (${ignoredCorrections.length})`}
                      </button>
                      {showIgnoredCorrections && (
                        <div className="mt-2 space-y-1.5">
                          <p className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">Archief — alleen audit-historie</p>
                          {ignoredCorrections.map(c => <CorrectionRow key={c.id} c={c} showActions={false} />)}
                        </div>
                      )}
                    </div>
                  )}

                  <button onClick={() => setShowCorrectionForm(v => !v)}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Nieuwe correctie toevoegen
                  </button>
                </div>
              </details>
            ) : null;
          })()}

          {/* New correction form */}
          {(showCorrectionForm || vatCorrections.length === 0) && (
            <div className="border border-dashed border-gray-300 rounded-lg p-3 space-y-2 bg-gray-50">
              <p className="text-xs font-medium text-gray-700">
                {vatCorrections.length === 0 ? "Geen BTW correcties — voeg toe indien van toepassing" : "Nieuwe BTW correctie"}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Oorspronkelijke periode</Label>
                  <Input className="text-xs h-7" placeholder="2025 of Q4 2025"
                    value={newCorrection.original_period}
                    onChange={e => setNewCorrection(p => ({ ...p, original_period: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Ingediend in periode</Label>
                  <Input className="text-xs h-7" placeholder="Q2 2026"
                    value={newCorrection.correction_period}
                    onChange={e => setNewCorrection(p => ({ ...p, correction_period: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div className="col-span-2">
                  <Label className="text-xs">Type</Label>
                  <Input className="text-xs h-7"
                    value={newCorrection.correction_type}
                    onChange={e => setNewCorrection(p => ({ ...p, correction_type: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">BTW-veld</Label>
                  <select className="w-full h-7 border border-gray-300 rounded px-1 text-xs bg-white"
                    value={newCorrection.affects_field}
                    onChange={e => setNewCorrection(p => ({ ...p, affects_field: e.target.value }))}>
                    <option value="">—</option>
                    <option value="1a">1a</option>
                    <option value="1b">1b</option>
                    <option value="1e">1e</option>
                    <option value="5a">5a</option>
                    <option value="5b">5b (voorbelasting)</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Bedrag (€)</Label>
                  <Input className="text-xs h-7" type="number" step="0.01"
                    value={newCorrection.amount}
                    onChange={e => setNewCorrection(p => ({ ...p, amount: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Reden</Label>
                <Input className="text-xs h-7"
                  value={newCorrection.reason}
                  onChange={e => setNewCorrection(p => ({ ...p, reason: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Notities (optioneel)</Label>
                <Input className="text-xs h-7"
                  value={newCorrection.notes}
                  onChange={e => setNewCorrection(p => ({ ...p, notes: e.target.value }))} />
              </div>
              <p className="text-[10px] text-gray-400">
                Stel <strong>BTW-veld</strong> op <strong>5b</strong> voor voorbelasting-correcties die meegenomen worden in de aangifte totalen.
              </p>
              <div className="flex gap-2">
                <Button size="sm" onClick={saveCorrection} className="h-7 text-xs">Opslaan</Button>
                {vatCorrections.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => setShowCorrectionForm(false)} className="h-7 text-xs">Annuleren</Button>
                )}
              </div>
            </div>
          )}

          {vatReport ? <VATReport data={vatReport} limit={vatLimit} onLimitChange={setVatLimit} onReceiptUploaded={handleReceiptUploaded} onRowUpdated={fetchVatReport} /> : (
            <div className="text-center py-8 text-gray-400">Select a quarter to generate the BTW report</div>
          )}
        </div>
      )}

      {/* MANUAL ENTRIES BREAKDOWN */}
      {activeTab === "manual" && (() => {
        const positiveEntries = manualEntries.filter(e => (e.amount || 0) >= 0);
        const negativeEntries = manualEntries.filter(e => (e.amount || 0) < 0);
        const totalPositive = positiveEntries.reduce((s, e) => s + (e.amount || 0), 0);
        const totalNegative = negativeEntries.reduce((s, e) => s + (e.amount || 0), 0);
        const netTotal = totalPositive + totalNegative;

        const exportCSV = () => {
          const headers = ["Date","Invoice #","Customer","Description","Amount (€)","Supplier Cost (€)","Gross Profit (€)","VAT Type","Notes"];
          const rows = manualEntries.map(e => {
            const gp = (e.amount_received || e.amount || 0) !== 0
              ? (e.amount || 0) - (e.supplier_cost || 0)
              : 0;
            return [
              e.date || "",
              e.invoice_number || "",
              e.customer_name || "",
              (e.description || "").replace(/,/g, ";"),
              (e.amount || 0).toFixed(2),
              (e.supplier_cost || 0).toFixed(2),
              gp.toFixed(2),
              e.vat_type || "",
              (e.notes || "").replace(/,/g, ";"),
            ];
          });
          const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
          const blob = new Blob([csv], { type: "text/csv" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `TBF_ManualEntries_${selectedQuarter || "all"}_${selectedYear}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        };

        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="text-base font-bold text-gray-800">Manual Service Entries Breakdown</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Review-only. No calculations or records are changed here.
                  Negative entries reduce Dashboard and VAT Report revenue.
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={fetchManualEntries}>Refresh</Button>
                <Button size="sm" variant="outline" onClick={exportCSV}>
                  <Download className="w-4 h-4 mr-1" /> Export CSV
                </Button>
              </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                <p className="text-xs font-medium text-green-700 uppercase tracking-wide">Total Positive</p>
                <p className="text-xl font-bold text-green-800 mt-1">
                  €{totalPositive.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-green-600 mt-0.5">{positiveEntries.length} entries</p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-xs font-medium text-red-700 uppercase tracking-wide">Total Negative</p>
                <p className="text-xl font-bold text-red-800 mt-1">
                  €{totalNegative.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-red-600 mt-0.5">{negativeEntries.length} entries</p>
              </div>
              <div className={`rounded-lg border p-3 ${netTotal >= 0 ? "border-blue-200 bg-blue-50" : "border-amber-200 bg-amber-50"}`}>
                <p className={`text-xs font-medium uppercase tracking-wide ${netTotal >= 0 ? "text-blue-700" : "text-amber-700"}`}>Net Total</p>
                <p className={`text-xl font-bold mt-1 ${netTotal >= 0 ? "text-blue-800" : "text-amber-800"}`}>
                  €{netTotal.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}
                </p>
                <p className={`text-xs mt-0.5 ${netTotal >= 0 ? "text-blue-600" : "text-amber-600"}`}>{manualEntries.length} total entries</p>
              </div>
            </div>

            {/* Table */}
            {manualEntries.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">No manual entries for this period.</div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600 uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Date</th>
                      <th className="px-3 py-2 text-left font-semibold">Invoice #</th>
                      <th className="px-3 py-2 text-left font-semibold">Customer</th>
                      <th className="px-3 py-2 text-left font-semibold">Description</th>
                      <th className="px-3 py-2 text-right font-semibold">Amount</th>
                      <th className="px-3 py-2 text-right font-semibold">Supplier Cost</th>
                      <th className="px-3 py-2 text-right font-semibold">Gross Profit</th>
                      <th className="px-3 py-2 text-left font-semibold">VAT Type</th>
                      <th className="px-3 py-2 text-left font-semibold">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {manualEntries.map((e, i) => {
                      const isNeg = (e.amount || 0) < 0;
                      const gp = (e.amount_received || e.amount || 0) !== 0
                        ? (e.amount || 0) - (e.supplier_cost || 0)
                        : 0;
                      const rowCls = isNeg
                        ? "bg-red-50 text-red-800"
                        : i % 2 === 0 ? "bg-white" : "bg-gray-50";
                      return (
                        <tr key={e.id || i} className={`${rowCls} border-b border-gray-100`}>
                          <td className="px-3 py-2 whitespace-nowrap">{e.date || "—"}</td>
                          <td className="px-3 py-2 whitespace-nowrap font-mono text-[10px]">{e.invoice_number || "—"}</td>
                          <td className="px-3 py-2 max-w-[120px] truncate" title={e.customer_name}>{e.customer_name || "—"}</td>
                          <td className="px-3 py-2 max-w-[180px] truncate" title={e.description}>{e.description || "—"}</td>
                          <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${isNeg ? "text-red-700" : "text-green-700"}`}>
                            €{(e.amount || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap text-gray-600">
                            {(e.supplier_cost || 0) > 0
                              ? `€${(e.supplier_cost || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}`
                              : "—"}
                          </td>
                          <td className={`px-3 py-2 text-right whitespace-nowrap font-semibold ${gp < 0 ? "text-red-600" : "text-blue-700"}`}>
                            {gp !== 0 ? `€${gp.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}` : "—"}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-500">{e.vat_type || "—"}</td>
                          <td className="px-3 py-2 max-w-[160px] truncate text-gray-500" title={e.notes}>{e.notes || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-bold text-gray-700 text-xs border-t-2 border-gray-300">
                      <td colSpan={4} className="px-3 py-2">Totals</td>
                      <td className={`px-3 py-2 text-right whitespace-nowrap ${netTotal < 0 ? "text-red-700" : "text-green-700"}`}>
                        €{netTotal.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap text-gray-600">
                        €{manualEntries.reduce((s, e) => s + (e.supplier_cost || 0), 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}
                      </td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* PROFIT REPORT */}
      {activeTab === "profit" && (
        <ProfitReport getAuthHeaders={getAuthHeaders} />
      )}

      {activeTab === "recurring" && (
        <RecurringTemplates getAuthHeaders={getAuthHeaders} />
      )}

      {showDashHelp && <HowCalculatedModal defaultTab="dashboard" onClose={() => setShowDashHelp(false)} />}

      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onImported={async () => {
            await Promise.all([fetchExpenses(), fetchAllocatedExpenses(), fetchVatReport()]);
          }}
        />
      )}
    </div>
    </DashboardLayout>
  );
}
